import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import styles from "./AdminDashboard.module.css";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { DollarSign, TrendingUp, Percent, Clock, CheckCircle, BarChart3, Search, AlertCircle, Calendar } from "lucide-react";
import { useQuery, useLazyQuery, useApolloClient } from "@apollo/client";
import {
 
  GET_TOP_SALE_TICKETS,
  GET_TOP_SALE_TICKETS_LEGACY,
  GET_PENDING_TICKETS,
  GET_PENDING_TICKETS_LEGACY,
  GET_REVENUE_ADMIN,
  GET_USERS_LIST,
  GET_REVENUE_SUPER_USER_DASHBOARD,
  GET_MARKETPLACES,
  GET_TICKET_STATUS_COUNTS,
  GET_TICKET_STATUS_COUNTS_DATED,
  GET_TICKET_STATUS_COUNTS_BY_USER,
  GET_LEAGUE_AND_TIMING_DATA,
  GET_LEAGUE_AND_TIMING_DATA_BY_USER,
  GET_LEAGUE_AND_TIMING_DATA_BY_CLIENT_TYPE,
  GET_SOLD_TICKETS_BY_MARKETPLACE,
  GET_SOLD_TICKETS_BY_MARKETPLACE_BY_USER,
  GET_SOLD_TICKETS_BY_MARKETPLACE_BY_CLIENT_TYPE,
  GET_SOLD_REVENUE_BY_SOLD_DATE,
  GET_SOLD_TICKET_PLACEMENTS_BY_SOLD_DATE,
  GET_REVENUE_BY_PRICING_MODE,
  GET_REVENUE_BY_PRICING_MODE_DATED,
  GET_USER_COUNTS_BY_TYPE,
  GET_SIGNUP_FUNNEL_DATA,
} from "./api/DashboardQuery";


import { GET_USER_TICKETS } from "../Userdetails/api/UserdetailsQuery";
import { GET_LEAGUES_LIST } from "../Tickets/api/TicketsQuery";
import {
  GET_MANAGE_TICKETS as GET_MANAGE_TICKETS_ALLOWLISTED,
  GET_LIST_TICKETS as GET_LIST_TICKETS_ALLOWLISTED,
  GET_SOLD_TICKETS as GET_SOLD_TICKETS_ALLOWLISTED,
  GET_DELIST_AND_RETURN_TICKETS as GET_DELIST_AND_RETURN_TICKETS_ALLOWLISTED,
  GET_DELIST_AND_UNSOLD_TICKETS as GET_DELIST_AND_UNSOLD_TICKETS_ALLOWLISTED,
} from "../Tickets/api/TicketsQuery";
import { GET_MANAGED_USERS_BY_SALES_REP, GET_SALES_REPS_BY_EMAIL } from "../Salesrep/api/SalesrepQuery";
import GraphModal from "../../components/GraphModal";
import { globalContext } from '../../Provider/AppProvider';
import {
  DashboardDatePeriod,
  DASHBOARD_DATE_PRESETS,
  formatDashboardDateRangeLabel,
  getDashboardDateRange,
  getDashboardDateRangeQueryBounds,
  isDashboardDateFilterActive,
  isTicketEventInDashboardRange,
  filterTicketsByDashboardEventDate,
  filterTicketsBySaleDate,
} from "./utils/dashboardDateRange";

interface TopSalesTicket {
  leagueName: string;
  totalCounts: number;
}

interface PendingTicketsData {
  getPendingActiveTicketsByLeague: Array<{
    activeTickets: number;
    leagueName: string;
  }>;
  getPendingActiveTicketsCountAndPayout: Array<{
    potentialPayout: number;
    totalCount: number;
  }>;
}

interface RevenueData {
  getRevenueForAdmin: Array<{
    monthsName: string;
    revenue: number;
    totalTickets: number;
  }>;
  getRevenueForAdminAggregate: Array<{
    overAllTickets: number;
    overAllSales: number;
    quickasystFee: number;
    avgListPrice: number;
  }>;
}

interface User {
  u_id: string;
  u_full_name: string;
  u_email_id: string;
  u_pricing_mode?: string;
}

interface LeagueSoldBreakdown {
  leagueName: string;
  soldCount: number;
  revenue: number;
  profit: number;
}

interface MarketplaceSoldBreakdown {
  name: string;
  soldCount: number;
  revenue: number;
  profit: number;
  vlFeeRate: number;
  vlFeeAmount: number;
}

interface UserRevenueData {
  getRevenueForSuperUser: Array<{ monthsName: string; revenue: number; totalTickets: number }>;
  getRevenueForSuperUserAggregate: Array<{ overAllTickets: number; overAllSales: number; quickasystFee: number; avgListPrice: number }>;
}
interface SalesRepRecord {
  sr_id: string;
  sr_commission_rate?: number | null;
}
interface ManagedUserRecord {
  u_id?: string;
  tickets_sold_amount?: number | string | null;
  u_total_earnings?: number | string | null;
}

const monthNamesFull = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const monthNamesShort = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const formatNumber = (value: number): string => value.toLocaleString('en-US');
const formatCurrency = (value: number, decimals = 2): string =>
  value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
const formatClientType = (raw: string) =>
  raw === 'self-pricing' ? 'Self Pricing' : raw === 'concierge' ? 'Concierge' : raw;

const Dashboard = () => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSellerMode, setSelectedSellerMode] = useState<"all" | "self-pricing" | "concierge">("all");
  const [signupFunnelPeriod, setSignupFunnelPeriod] = useState<7 | 30 | 90>(30);
  const [dashboardDatePeriod, setDashboardDatePeriod] = useState<DashboardDatePeriod>('All');
  const [customDashboardStart, setCustomDashboardStart] = useState('');
  const [customDashboardEnd, setCustomDashboardEnd] = useState('');
  const [showDashboardDatePicker, setShowDashboardDatePicker] = useState(false);
  const dashboardDateRef = useRef<HTMLDivElement | null>(null);
  const dateRef = useRef<HTMLInputElement | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [selectedUserClientType, setSelectedUserClientType] = useState<string>("");

  const { globalState } = useContext<any>(globalContext);
  const userId = globalState?.profile?.id || localStorage.getItem("adminUserId") || "";
  const userRole = globalState?.profile?.userRole;
  const loggedInEmail = (
    globalState?.profile?.mailId ||
    localStorage.getItem("adminMailId") ||
    ""
  ).toLowerCase().trim();
  const isAffiliateAdmin = userRole === "affiliater_admin";
  const isMarketingUser = false;
  const useLegacyDashboardQueries = isAffiliateAdmin;

  const activeDate = useMemo(() => {
    if (!selectedDate) return new Date();
    const date = new Date(selectedDate);
    return Number.isNaN(date.getTime()) ? new Date() : date;
  }, [selectedDate]);

  const activeMonthNumber = activeDate.getMonth() + 1;
  const activeMonthName = monthNamesShort[activeDate.getMonth()];
  const activeMonthLabel = monthNamesFull[activeDate.getMonth()];
  const activeYear = activeDate.getFullYear().toString();
  const activePreviousYear = (activeDate.getFullYear() - 1).toString();
  const selectedDateLabel = selectedDate
    ? activeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Filter';

  const dashboardDateRange = useMemo(
    () => getDashboardDateRange(dashboardDatePeriod, customDashboardStart, customDashboardEnd),
    [dashboardDatePeriod, customDashboardStart, customDashboardEnd],
  );
  const dashboardDateFilterActive = isDashboardDateFilterActive(dashboardDatePeriod) && !!dashboardDateRange;
  const useEventDateAggregatesForMetrics = dashboardDateFilterActive;
  const dashboardDateLabel = formatDashboardDateRangeLabel(
    dashboardDatePeriod,
    customDashboardStart,
    customDashboardEnd,
  );
  
  const [openGraph, setOpenGraph] = useState(false);
  const [graphTitle, setGraphTitle] = useState("");
  const [graphValue, setGraphValue] = useState(0);
  const [graphDataType, setGraphDataType] = useState("");
  const searchDebounceRef = useRef<number | null>(null);
  const [shouldLoadDeferredSections, setShouldLoadDeferredSections] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const apolloClient = useApolloClient();

  useEffect(() => {
    const timer = window.setTimeout(() => setShouldLoadDeferredSections(true), 2500);
    return () => window.clearTimeout(timer);
  }, []);

  // Fetch Users for Search
  const [searchUsers, { data: usersData, loading: searchLoading }] = useLazyQuery(GET_USERS_LIST, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setSearchResults(data?.user || []);
      if (searchQuery.trim().length > 0) {
        setShowSearchResults(true);
      }
    },
    onError: (error) => {
      console.error('[AdminDashboard] Search error:', error);
      setSearchResults([]);
      setShowSearchResults(false);
    },
  });

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = window.setTimeout(() => {
      searchUsers({ variables: { searchText: `%${searchQuery.trim()}%` } });
    }, 250);

    return () => {
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery, searchUsers]);

  // IMPORTANT FIX: When user is selected, we need to pass userId to ALL queries
  // But for client type filtering, we need to use the selected user's client type
  const effectiveUserId = isMarketingUser ? userId : (selectedUserId || userId);
  const effectiveUserRole = isMarketingUser ? "user" : (selectedUserId ? "user" : userRole);
  
  // For client type filtering - use selected user's client type if available
  const normalizePricingMode = (mode: string | null | undefined) => {
    if (!mode) return null;
    if (mode === 'self-pricing' || mode === 'selfPricing' || mode === 'self_pricing') return 'self-pricing';
    if (mode === 'concierge') return 'concierge';
    return null;
  };
  
  const effectiveClientType = selectedUserId && selectedUserClientType 
    ? normalizePricingMode(selectedUserClientType)
    : normalizePricingMode(selectedSellerMode);

  const selectedUserPricingMode = selectedUserId ? normalizePricingMode(selectedUserClientType) : null;

  // Debug info for filter and data
  useEffect(() => {
    console.group('[AdminDashboard] 🔍 Filter State Changed');
    console.log('selectedSellerMode:', selectedSellerMode);
    console.log('selectedUserId:', selectedUserId);
    console.log('selectedUserName:', selectedUserName);
    console.log('selectedUserClientType:', selectedUserClientType);
    console.log('selectedUserPricingMode:', selectedUserPricingMode);
    console.log('effectiveClientType:', effectiveClientType);
    console.log('shouldUseClientTypeFilter:', !selectedUserId && selectedSellerMode !== 'all');
    console.log('shouldUseUserFilter:', !!selectedUserId);
    console.groupEnd();
  }, [selectedSellerMode, effectiveClientType, selectedUserId, selectedUserName, selectedUserClientType, selectedUserPricingMode]);

  // Fetch Marketplaces
  const { data: marketplacesData, loading: marketplacesLoading } = useQuery(GET_MARKETPLACES, {
    fetchPolicy: 'cache-first',
    skip: isMarketingUser,
    onError: (err) => console.error("🔴 Marketplaces Error:", err),
  });

  const marketplaces: Array<{ id: string; name: string; vl_fee_percentage: number; active: boolean; qa_nets: string }> =
    Array.isArray(marketplacesData?.marketplaces) ? marketplacesData.marketplaces : [];

  const activeMarketplacesForFee = marketplaces.filter(m => m.active);

  // Fetch Top Sales Tickets - FIXED: Use effectiveUserId
  const { 
    data: topSalesData, 
    loading: topSalesLoading,
  } = useQuery((useLegacyDashboardQueries ? GET_TOP_SALE_TICKETS_LEGACY : GET_TOP_SALE_TICKETS), {
    variables: useLegacyDashboardQueries
      ? {
          month: activeMonthName,
          year: activeYear,
        }
      : {
          month: activeMonthName,
          year: activeYear,
          userId: effectiveUserId,
          userRole: effectiveUserRole
        },
    fetchPolicy: 'network-only',
    skip: isMarketingUser ? true : (useLegacyDashboardQueries ? false : !effectiveUserId || !effectiveUserRole),
    onError: (err) => console.error("🔴 Top Sales Error:", err),
  });
const getMarketplaceName = (raw: any): string => {
  if (!raw) return 'Unknown';
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    // Try to match case‑insensitively with known marketplace names
    const match = marketplaces.find(mp => mp.name.toLowerCase() === trimmed.toLowerCase());
    return match ? match.name : trimmed;
  }
  if (typeof raw === 'object') {
    const values = Object.values(raw).filter(Boolean).join(' / ');
    const trimmed = values.trim();
    const match = marketplaces.find(mp => mp.name.toLowerCase() === trimmed.toLowerCase());
    return match ? match.name : trimmed || 'Unknown';
  }
  return 'Unknown';
};

const normalizeMarketplaceName = (name: string) => String(name || '').trim().toLowerCase();

const marketplaceFeeLookup = useMemo(() => {
  const map = new Map<string, { name: string; vl_fee_percentage: number }>();
  marketplaces.forEach((mp) => {
    const normalized = normalizeMarketplaceName(mp.name);
    map.set(normalized, {
      name: mp.name,
      vl_fee_percentage: Number(mp.vl_fee_percentage || 0),
    });
  });
  return map;
}, [marketplaces]);

// console.log('🔍 Raw marketplace sold (from filterlisttickets):', rawMarketplaceSold);
  // Fetch Pending Tickets - FIXED: Use effectiveUserId
  const { 
    data: pendingTicketsData, 
    loading: pendingLoading,
  } = useQuery<any>(useLegacyDashboardQueries ? GET_PENDING_TICKETS_LEGACY : GET_PENDING_TICKETS, {
    variables: useLegacyDashboardQueries
      ? undefined
      : {
          userId: effectiveUserId,
          userRole: effectiveUserRole
        },
    fetchPolicy: 'network-only',
    skip: isMarketingUser ? true : (useLegacyDashboardQueries ? false : !effectiveUserId || !effectiveUserRole),
    onError: (err) => console.error("🔴 Pending Tickets Error:", err),
  });

  // Fetch Revenue Data - FIXED: Use appropriate query based on user selection
  const shouldUseSuperUserQuery = !!selectedUserId || isMarketingUser;
  
  const { 
    data: revenueAdminData, 
    loading: revenueLoading,
  } = useQuery<RevenueData>(GET_REVENUE_ADMIN, {
    variables: {
      startYear: parseInt(activePreviousYear),
      endYear: parseInt(activeYear),
      startMonth: "January",
      endMonth: activeMonthName,
    },
    fetchPolicy: 'network-only',
    skip: shouldUseSuperUserQuery || isAffiliateAdmin || useEventDateAggregatesForMetrics,
    onError: (err) => console.error("🔴 Revenue Admin Error:", err),
  });

 const { 
  data: userRevenueData, 
  loading: userRevenueLoading,
  refetch: refetchUserRevenue 
} = useQuery<UserRevenueData>(GET_REVENUE_SUPER_USER_DASHBOARD, {
  variables: {
    startYear: parseInt(activePreviousYear),
    endYear: parseInt(activeYear),
    startMonth: "January",
    endMonth: activeMonthName,
    userId: effectiveUserId,
  },
  fetchPolicy: 'network-only',
  skip: isMarketingUser || !shouldUseSuperUserQuery || !effectiveUserId || useEventDateAggregatesForMetrics,
  onError: (err) => console.error("🔴 User Revenue Error:", err),
});

  // Sales rep scoped fallback for environments where dashboard custom functions are unavailable.
  const { data: salesRepByEmailData, loading: salesRepByEmailLoading } = useQuery<{ sales_reps: SalesRepRecord[] }>(GET_SALES_REPS_BY_EMAIL, {
    variables: { email: loggedInEmail },
    fetchPolicy: "network-only",
    skip: !isMarketingUser || !loggedInEmail,
  });
  const salesRepIdForDashboard = salesRepByEmailData?.sales_reps?.[0]?.sr_id || null;
  const salesRepCommissionRate = Number(salesRepByEmailData?.sales_reps?.[0]?.sr_commission_rate || 0);

  const { data: managedUsersByRepData, loading: managedUsersByRepLoading } = useQuery<{ managedUsers: ManagedUserRecord[] }>(GET_MANAGED_USERS_BY_SALES_REP, {
    variables: { salesRepId: salesRepIdForDashboard },
    fetchPolicy: "network-only",
    skip: !isMarketingUser || !salesRepIdForDashboard,
  });
  const managedUserIdsForDashboard = useMemo(
    () => (managedUsersByRepData?.managedUsers || []).map((u: any) => u?.u_id).filter(Boolean),
    [managedUsersByRepData]
  );
  // Use allowlisted ManageTickets queries for marketing_user status counts.
  const marketingScopedVars = {
    pageSize: 5000,
    pageOffset: 0,
    search_event: "%",
    order_by: [{ tp_updated_at: "desc" as const }, { tp_id: "asc" as const }],
  };
  const { data: marketingVerificationData, loading: marketingVerificationLoading } = useQuery<any>(GET_MANAGE_TICKETS_ALLOWLISTED, {
    variables: marketingScopedVars,
    fetchPolicy: "network-only",
    skip: !isMarketingUser || managedUserIdsForDashboard.length === 0,
  });
  const { data: marketingListedData, loading: marketingListedLoading } = useQuery<any>(GET_LIST_TICKETS_ALLOWLISTED, {
    variables: marketingScopedVars,
    fetchPolicy: "network-only",
    skip: !isMarketingUser || managedUserIdsForDashboard.length === 0,
  });
  const { data: marketingSoldData, loading: marketingSoldLoading } = useQuery<any>(GET_SOLD_TICKETS_ALLOWLISTED, {
    variables: marketingScopedVars,
    fetchPolicy: "network-only",
    skip: !isMarketingUser || managedUserIdsForDashboard.length === 0,
  });
  const { data: marketingDelistedData, loading: marketingDelistedLoading } = useQuery<any>(GET_DELIST_AND_RETURN_TICKETS_ALLOWLISTED, {
    variables: marketingScopedVars,
    fetchPolicy: "network-only",
    skip: false,
  });
  const { data: marketingUnsoldData, loading: marketingUnsoldLoading } = useQuery<any>(GET_DELIST_AND_UNSOLD_TICKETS_ALLOWLISTED, {
    variables: marketingScopedVars,
    fetchPolicy: "network-only",
    skip: !isMarketingUser || managedUserIdsForDashboard.length === 0,
  });

  const shouldSkipAllModeTicketStatusCounts =
    !!selectedUserId ||
    isMarketingUser ||
    dashboardDateFilterActive ||
    (!selectedUserId && selectedSellerMode !== 'all');

  // Fetch ticket status counts for dashboard (All mode only — SP/CX filters use pricing-mode aggregates).
  const { data: ticketStatusDataAll, loading: ticketStatusLoading } = useQuery(GET_TICKET_STATUS_COUNTS, {
    fetchPolicy: 'network-only',
    skip: shouldSkipAllModeTicketStatusCounts,
    onError: (err) => console.error("🔴 Ticket Status Counts Error:", err),
  });

  const { data: ticketStatusDataDated, loading: ticketStatusDatedLoading } = useQuery(GET_TICKET_STATUS_COUNTS_DATED, {
    variables: {
      startdate: dashboardDateRange?.startDate,
      enddate: dashboardDateRange?.endDate,
    },
    fetchPolicy: 'network-only',
    skip: !!selectedUserId || isMarketingUser || !dashboardDateFilterActive,
    onError: (err) => console.error("🔴 Ticket Status Counts (dated) Error:", err),
  });

  const ticketStatusData = dashboardDateFilterActive ? ticketStatusDataDated : ticketStatusDataAll;
  const ticketStatusMetricsLoading = dashboardDateFilterActive ? ticketStatusDatedLoading : ticketStatusLoading;

  // Fetch all tickets for selected user (for status counts)
  const { data: userTicketsData, loading: userTicketsLoading } = useQuery(GET_USER_TICKETS, {
    variables: { userId: isMarketingUser ? effectiveUserId : selectedUserId },
    fetchPolicy: 'network-only',
    skip: isMarketingUser || !(selectedUserId || isMarketingUser),
    onError: (err) => console.error("🔴 User Tickets Error:", err),
  });

  const userTicketsInDashboardRange = useMemo(() => {
    const tickets = userTicketsData?.tickets || [];
    if (!dashboardDateFilterActive || !dashboardDateRange) {
      return tickets;
    }
    return tickets.filter((ticket: { t_event_date?: string }) =>
      isTicketEventInDashboardRange(
        ticket.t_event_date,
        dashboardDateRange.startDate,
        dashboardDateRange.endDate,
      ),
    );
  }, [userTicketsData, dashboardDateFilterActive, dashboardDateRange]);

  // Fetch per-league sold revenue data - FIXED: Use appropriate data source based on filters
  const shouldUseClientTypeFilter = !isMarketingUser && !selectedUserId && selectedSellerMode !== 'all';
  const shouldUseUserFilter = isMarketingUser || !!selectedUserId;
  
  const { data: leagueTimingData, loading: leagueTimingLoading } = useQuery(GET_LEAGUE_AND_TIMING_DATA, {
    fetchPolicy: 'network-only',
    skip: shouldUseUserFilter || shouldUseClientTypeFilter,
    onError: (err) => console.error("🔴 League/Timing Error:", err),
  });

  const { data: userLeagueTimingData, loading: userLeagueTimingLoading } = useQuery(GET_LEAGUE_AND_TIMING_DATA_BY_USER, {
    variables: { userId: effectiveUserId },
    fetchPolicy: 'network-only',
    skip: isMarketingUser || !shouldUseUserFilter || !effectiveUserId,
    onError: (err) => console.error("🔴 User League/Timing Error:", err),
  });

  const { data: clientTypeLeagueRaw, loading: clientTypeLeagueLoading, refetch: refetchClientTypeLeague } = useQuery(GET_LEAGUE_AND_TIMING_DATA_BY_CLIENT_TYPE, {
    variables: { clientType: effectiveClientType },
    fetchPolicy: 'network-only',
    skip: !shouldUseClientTypeFilter || !effectiveClientType,
    onError: (err) => console.error("🔴 Client Type League Error:", err),
  });

  // Fetch marketplace sold data
  const { data: marketplaceSoldData, loading: marketplaceSoldLoading } = useQuery(GET_SOLD_TICKETS_BY_MARKETPLACE, {
    fetchPolicy: 'network-only',
    skip: shouldUseUserFilter || shouldUseClientTypeFilter,
    onError: (err) => console.error("🔴 Marketplace Sold Error:", err),
  });

  const { data: userMarketplaceSoldData, loading: userMarketplaceSoldLoading } = useQuery(GET_SOLD_TICKETS_BY_MARKETPLACE_BY_USER, {
    variables: { userId: effectiveUserId },
    fetchPolicy: 'network-only',
    skip: isMarketingUser || !shouldUseUserFilter || !effectiveUserId,
    onError: (err) => console.error("🔴 User Marketplace Sold Error:", err),
  });

  const { data: clientTypeMarketplaceRaw, loading: clientTypeMarketplaceLoading, refetch: refetchClientTypeMarketplace } = useQuery(GET_SOLD_TICKETS_BY_MARKETPLACE_BY_CLIENT_TYPE, {
    variables: { clientType: effectiveClientType },
    fetchPolicy: 'network-only',
    skip: !shouldUseClientTypeFilter || !effectiveClientType,
    onError: (err) => console.error("🔴 Client Type Marketplace Error:", err),
  });

const userDatedSoldMetrics = useMemo(() => {
  if (!selectedUserId || !dashboardDateFilterActive || !dashboardDateRange) {
    return null;
  }
  // ← USE SALE DATE for financial data filtering
  const rows = filterTicketsBySaleDate(
    (userMarketplaceSoldData?.soldByMarketplace || []) as Array<{
      tp_sold_at?: string | null;
      tp_sold_amount?: number;
      tp_quick_cut_amount?: number;
    }>,
    true,
    dashboardDateRange,
  );
  return rows.reduce(
    (acc, row) => ({
      revenue: acc.revenue + Number(row.tp_sold_amount || 0),
      profit: acc.profit + Number(row.tp_quick_cut_amount || 0),
      count: acc.count + 1,
    }),
    { revenue: 0, profit: 0, count: 0 },
  );
}, [selectedUserId, dashboardDateFilterActive, dashboardDateRange, userMarketplaceSoldData]);
  // Fetch revenue by pricing mode — all-time cached for avg fallback; dated when filter active
  const { data: pricingModeDataAll, loading: pricingModeLoading } = useQuery(GET_REVENUE_BY_PRICING_MODE, {
    fetchPolicy: 'cache-first',
    skip: isMarketingUser,
    onError: (err) => console.error("🔴 Pricing Mode Revenue Error:", err),
  });

  const { data: pricingModeDataDated, loading: pricingModeDatedLoading } = useQuery(GET_REVENUE_BY_PRICING_MODE_DATED, {
    variables: {
      startdate: dashboardDateRange?.startDate,
      enddate: dashboardDateRange?.endDate,
    },
    fetchPolicy: 'network-only',
    skip: isMarketingUser || !dashboardDateFilterActive,
    onError: (err) => console.error("🔴 Pricing Mode Revenue (dated) Error:", err),
  });

  const pricingModeData = dashboardDateFilterActive ? pricingModeDataDated : pricingModeDataAll;
  const pricingModeMetricsLoading = dashboardDateFilterActive ? pricingModeDatedLoading : pricingModeLoading;

  // Separate query for user counts (user_aggregate may have different role permissions)
  const { data: userCountsData } = useQuery(GET_USER_COUNTS_BY_TYPE, {
    fetchPolicy: 'network-only',
    skip: isMarketingUser || !shouldLoadDeferredSections,
    onError: (err) => console.error("🔴 User Counts Error:", err),
  });

  useEffect(() => {
    if (!pricingModeData) {
      console.warn('[AdminDashboard] ⚠️ pricingModeData is undefined/null');
      return;
    }
    console.group('[AdminDashboard] 📊 pricingModeData received');
    console.log('raw pricingModeData:', pricingModeData);
    console.log('SP revenue:', pricingModeData?.selfPricing?.aggregate?.sum?.tp_sold_amount);
    console.log('SP sold count:', pricingModeData?.selfPricing?.aggregate?.count);
    console.log('SP pending potential:', pricingModeData?.selfPricingPending?.aggregate?.sum?.tp_list_price);
    console.log('SP toBeVerified:', pricingModeData?.spToBeVerified?.aggregate?.count);
    console.log('SP verified:', pricingModeData?.spVerified?.aggregate?.count);
    console.log('SP listed:', pricingModeData?.spListed?.aggregate?.count);
    console.log('SP soldTickets:', pricingModeData?.spSoldTickets?.aggregate?.count);
    console.log('SP delisted:', pricingModeData?.spDelisted?.aggregate?.count);
    console.log('SP unsold:', pricingModeData?.spUnsold?.aggregate?.count);
    console.log('CX revenue:', pricingModeData?.concierge?.aggregate?.sum?.tp_sold_amount);
    console.log('CX sold count:', pricingModeData?.concierge?.aggregate?.count);
    console.log('SP user count:', pricingModeData?.selfPricingUsers?.aggregate?.count);
    console.log('CX user count:', pricingModeData?.conciergeUsers?.aggregate?.count);
    console.log('Untagged user count:', pricingModeData?.untaggedUsers?.aggregate?.count);
    console.groupEnd();
  }, [pricingModeData]);

  useEffect(() => {
    console.group('[AdminDashboard] 🗺️ Client Type League/Marketplace Data');
    console.log('effectiveClientType:', effectiveClientType);
    console.log('isModeFiltered:', isModeFiltered);
    console.log('selectedSellerMode:', selectedSellerMode);
    console.log('clientTypeLeagueRaw:', clientTypeLeagueRaw);
    console.log('clientTypeLeagueRaw?.soldByLeagueRaw length:', clientTypeLeagueRaw?.soldByLeagueRaw?.length);
    console.log('clientTypeMarketplaceRaw:', clientTypeMarketplaceRaw);
    console.log('clientTypeLeagueLoading:', clientTypeLeagueLoading);
    console.log('clientTypeMarketplaceLoading:', clientTypeMarketplaceLoading);
    if (clientTypeLeagueRaw?.soldByLeagueRaw && clientTypeLeagueRaw.soldByLeagueRaw.length > 0) {
      console.log('Sample league record:', clientTypeLeagueRaw.soldByLeagueRaw[0]);
    }
    console.groupEnd();
  }, [clientTypeLeagueRaw, clientTypeMarketplaceRaw, clientTypeLeagueLoading, clientTypeMarketplaceLoading, effectiveClientType]);

  // FIXED: Trigger refetch when client type filter changes
  useEffect(() => {
    if (shouldUseClientTypeFilter && effectiveClientType) {
      console.group('[AdminDashboard] 🔄 Client Type Filter Changed');
      console.log('🔍 New Filter Settings:', {
        selectedSellerMode,
        effectiveClientType,
        shouldUseClientTypeFilter
      });
      console.log('🔄 Refetching client type league and marketplace data...');
      
      // Trigger refetch for updated data
      refetchClientTypeLeague({ clientType: effectiveClientType });
      refetchClientTypeMarketplace({ clientType: effectiveClientType });
      
      console.log('✅ Refetch triggered, awaiting new data...');
      console.groupEnd();
    }
  }, [selectedSellerMode, effectiveClientType, shouldUseClientTypeFilter, refetchClientTypeLeague, refetchClientTypeMarketplace]);

  // Helper to extract readable marketplace name
  // const getMarketplaceName = (name: any) => {
  //   if (typeof name === 'string') return name;
  //   if (typeof name === 'object' && name !== null) {
  //     return Object.values(name).filter(Boolean).join(' / ') || 'Unknown';
  //   }
  //   return 'Unknown';
  // };

  // Signup funnel
  const signupFunnelCutoff = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - signupFunnelPeriod);
    return d.toISOString();
  }, [signupFunnelPeriod]);

  const { data: signupFunnelData, loading: signupFunnelLoading } = useQuery(GET_SIGNUP_FUNNEL_DATA, {
    variables: { cutoffDate: signupFunnelCutoff },
    fetchPolicy: 'network-only',
    skip: !shouldLoadDeferredSections,
    onError: (err) => console.error("🔴 Signup Funnel Error:", err),
  });

  const { data: leaguesListData, loading: leaguesListLoading } = useQuery<{ leagues: Array<{ l_id: string; l_name: string }> }>(GET_LEAGUES_LIST, {
    fetchPolicy: 'cache-first',
    onError: (err) => console.error("🔴 Leagues List Error:", err),
  });

  const leagues = leaguesListData?.leagues;
  const allLeagues = Array.isArray(leagues)
    ? leagues.map((league) => league.l_name)
    : [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dashboardDateRef.current && !dashboardDateRef.current.contains(event.target as Node)) {
        setShowDashboardDatePicker(false);
      }
    };
    if (showDashboardDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDashboardDatePicker]);

  const handleDashboardDatePreset = (period: DashboardDatePeriod) => {
    setDashboardDatePeriod(period);
    if (period !== 'Custom') {
      setShowDashboardDatePicker(false);
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await apolloClient.refetchQueries({ include: 'active' });
    } catch (refreshError) {
      console.error('[AdminDashboard] Refresh failed:', refreshError);
    } finally {
      setIsRefreshing(false);
    }
  };

  // FIXED: Enhanced search handler with better filtering
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim() === "") {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

 const handleUserSelect = (user: User) => {
  console.log('[AdminDashboard] 👤 User Selected:', user);
  setSelectedUserId(user.u_id);
  setSelectedUserName(user.u_full_name);
  setSelectedUserClientType(user.u_pricing_mode || '');
  setSearchQuery("");
  setSearchResults([]);
  setShowSearchResults(false);
  // Reset seller mode when user is selected to avoid confusion
  setSelectedSellerMode("all");
  
  // 🔥 Trigger refetch for user revenue data
  if (refetchUserRevenue) {
    refetchUserRevenue({
      userId: user.u_id,
      startYear: parseInt(activePreviousYear),
      endYear: parseInt(activeYear),
      startMonth: "January",
      endMonth: activeMonthName,
    });
    console.log('[AdminDashboard] 🔄 Refetching revenue data for user:', user.u_id);
  }
};

  const handleClearUser = () => {
    console.log('[AdminDashboard] 🧹 Clearing user filter');
    setSelectedUserId(null);
    setSelectedUserName("");
    setSelectedUserClientType('');
    setSearchQuery("");
    setSearchResults([]);
    setShowSearchResults(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };
    
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleOpenDatePicker = () => {
    dateRef.current?.showPicker?.();
    dateRef.current?.click();
  };

  const handleClearSelectedDate = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    setSelectedDate("");
  };

  const handleClearDashboardDateFilter = () => {
    setDashboardDatePeriod('All');
    setCustomDashboardStart('');
    setCustomDashboardEnd('');
    setShowDashboardDatePicker(false);
  };

  const openGraphModal = (title: string, value: number, dataType: string) => {
    setGraphTitle(title);
    setGraphValue(value);
    setGraphDataType(dataType);
    setOpenGraph(true);
  };

  // Extract data based on current filters - FIXED: Proper data extraction
  const pendingSource = isAffiliateAdmin
    ? pendingTicketsData?.getPendingTicketsDashboard
    : isMarketingUser
    ? null
    : pendingTicketsData;
  const pendingTickets: PendingTicketsData["getPendingActiveTicketsCountAndPayout"][number] | null =
    pendingSource?.getPendingActiveTicketsCountAndPayout?.[0] || null;
  const pendingByLeague: PendingTicketsData["getPendingActiveTicketsByLeague"] =
    pendingSource?.getPendingActiveTicketsByLeague || [];
  const topSales: TopSalesTicket[] = isAffiliateAdmin
    ? (Array.isArray(topSalesData?.getTopSalesTickets) ? topSalesData.getTopSalesTickets : [])
    : isMarketingUser
    ? []
    : (Array.isArray(topSalesData?.getTopSales) ? topSalesData.getTopSales : []);

// Revenue aggregation - Use useMemo for better reactivity
const revenueAggregate = useMemo(() => {
  if ((selectedUserId || isMarketingUser) && userRevenueData?.getRevenueForSuperUserAggregate?.[0]) {
    return userRevenueData.getRevenueForSuperUserAggregate[0];
  }
  if (!selectedUserId && revenueAdminData?.getRevenueForAdminAggregate?.[0]) {
    return revenueAdminData.getRevenueForAdminAggregate[0];
  }
  return null;
}, [selectedUserId, userRevenueData, revenueAdminData]);

  // Debug: log revenue sources when user selected
  useEffect(() => {
    if (selectedUserId) {
      console.log('[AdminDashboard][DEBUG] effectiveUserId:', effectiveUserId);
      console.log('[AdminDashboard][DEBUG] userRevenueData aggregate:', userRevenueData?.getRevenueForSuperUserAggregate);
      console.log('[AdminDashboard][DEBUG] revenueAggregate used:', revenueAggregate);
    }
  }, [selectedUserId, effectiveUserId, userRevenueData, revenueAggregate]);

  const revenueByMonth = (selectedUserId || isMarketingUser)
    ? (userRevenueData?.getRevenueForSuperUser || [])
    : (revenueAdminData?.getRevenueForAdmin || []);

  const selectedMonthData = Array.isArray(revenueByMonth)
    ? [...revenueByMonth].reverse().find((item: { monthsName: string }) => {
        const normalizedName = item?.monthsName?.trim();
        return [activeMonthName, activeMonthLabel, activeMonthLabel.slice(0, 3)].includes(normalizedName);
      })
    : undefined;

  // Calculate revenue - fallback to pricingModeData when getRevenueForAdmin returns null.
  const _fallbackRevenue = (pricingModeData?.selfPricing?.aggregate?.sum?.tp_sold_amount ?? 0) +
    (pricingModeData?.concierge?.aggregate?.sum?.tp_sold_amount ?? 0);
  const _fallbackProfit = (pricingModeData?.selfPricing?.aggregate?.sum?.tp_quick_cut_amount ?? 0) +
    (pricingModeData?.concierge?.aggregate?.sum?.tp_quick_cut_amount ?? 0);
  const _fallbackSoldCount = (pricingModeData?.selfPricing?.aggregate?.count ?? 0) +
    (pricingModeData?.concierge?.aggregate?.count ?? 0);

  // When dashboard date filter is active, prefer sale-date (tp_sold_at) aggregates
  const saleDateRangeBounds = useMemo(() => {
    if (!dashboardDateFilterActive || !dashboardDateRange) {
      return null;
    }
    return getDashboardDateRangeQueryBounds(dashboardDateRange.startDate, dashboardDateRange.endDate);
  }, [dashboardDateFilterActive, dashboardDateRange]);

  const soldDateRangeStart = saleDateRangeBounds?.startDateTime ?? null;
  const soldDateRangeEnd = saleDateRangeBounds?.endDateTime ?? null;

  const { data: soldBySaleDateData } = useQuery(GET_SOLD_REVENUE_BY_SOLD_DATE, {
    variables: {
      startdate: soldDateRangeStart,
      enddate: soldDateRangeEnd,
    },
    skip: !dashboardDateFilterActive || !soldDateRangeStart || !soldDateRangeEnd,
    fetchPolicy: 'network-only',
  });

  const { data: soldPlacementsBySaleDateData } = useQuery(GET_SOLD_TICKET_PLACEMENTS_BY_SOLD_DATE, {
    variables: {
      startdate: soldDateRangeStart,
      enddate: soldDateRangeEnd,
    },
    skip: !dashboardDateFilterActive || !soldDateRangeStart || !soldDateRangeEnd,
    fetchPolicy: 'network-only',
  });

  const soldAgg = soldBySaleDateData?.soldAgg?.aggregate;
  const soldPlacements = soldPlacementsBySaleDateData?.ticket_placement || [];

  const exactSaleDateTotals = soldPlacements.reduce((acc: { revenue: number; profit: number; count: number }, row: any) => {
    const revenue = Number(row?.tp_logitix_amount ?? 0);
    const profit = Number(row?.tp_quick_cut_amount ?? 0);
    return {
      revenue: Number((acc.revenue + revenue).toFixed(2)),
      profit: Number((acc.profit + profit).toFixed(2)),
      count: acc.count + 1,
    };
  }, { revenue: 0, profit: 0, count: 0 });

  const datedEventRevenue = (dashboardDateFilterActive && soldAgg)
    ? exactSaleDateTotals.revenue
    : (userDatedSoldMetrics?.revenue ?? _fallbackRevenue);

  const datedEventProfit = (dashboardDateFilterActive && soldAgg)
    ? exactSaleDateTotals.profit
    : (userDatedSoldMetrics?.profit ?? _fallbackProfit);

  const datedEventSoldCount = (dashboardDateFilterActive && soldAgg)
    ? exactSaleDateTotals.count
    : (userDatedSoldMetrics?.count ?? _fallbackSoldCount);

  const baseTotalRevenue = useEventDateAggregatesForMetrics
    ? datedEventRevenue
    : (selectedDate && selectedMonthData?.revenue != null
      ? Number(selectedMonthData.revenue)
      : (revenueAggregate?.overAllSales != null ? Number(revenueAggregate.overAllSales) : _fallbackRevenue));
  
  const baseQaProfit = useEventDateAggregatesForMetrics
    ? datedEventProfit
    : (revenueAggregate?.quickasystFee != null
      ? Number(revenueAggregate.quickasystFee)
      : _fallbackProfit);
  const baseVlFee = 0;
  const baseUserPayout = Math.max(0, baseTotalRevenue - baseQaProfit);
  const baseTotalTickets = useEventDateAggregatesForMetrics
    ? datedEventSoldCount
    : (selectedDate && selectedMonthData?.totalTickets != null
      ? Number(selectedMonthData.totalTickets)
      : (revenueAggregate?.overAllTickets != null ? Number(revenueAggregate.overAllTickets) : 0));
  const baseAvgTicketPrice = useEventDateAggregatesForMetrics
    ? (datedEventSoldCount > 0 ? datedEventRevenue / datedEventSoldCount : 0)
    : (revenueAggregate?.avgListPrice != null ? Number(revenueAggregate.avgListPrice) : 0);
  const basePendingTicketsCount = pendingTickets?.totalCount || 0;

  const baseSoldTickets = topSales.reduce((acc: number, item: TopSalesTicket) => acc + (item.totalCounts ?? 0), 0);
  const basePendingFromLeagues = pendingByLeague.reduce((acc: number, item) => acc + (item.activeTickets ?? 0), 0);

  // Get pricing mode data - FIXED: Properly extract from API response
  const spRevenue = pricingModeData?.selfPricing?.aggregate?.sum?.tp_sold_amount || 0;
  const spProfit = pricingModeData?.selfPricing?.aggregate?.sum?.tp_quick_cut_amount || 0;
  const spSoldCount = pricingModeData?.selfPricing?.aggregate?.count || 0;
  const spPendingCount = (pricingModeData?.selfPricingPendingManage?.aggregate?.count || 0) + (pricingModeData?.selfPricingPendingList?.aggregate?.count || 0);
  const spPendingPotential = (pricingModeData?.selfPricingPendingManage?.aggregate?.sum?.tp_list_price || 0) + (pricingModeData?.selfPricingPendingList?.aggregate?.sum?.tp_list_price || 0);
  const cxRevenue = pricingModeData?.concierge?.aggregate?.sum?.tp_sold_amount || 0;
  const cxProfit = pricingModeData?.concierge?.aggregate?.sum?.tp_quick_cut_amount || 0;
  const cxSoldCount = pricingModeData?.concierge?.aggregate?.count || 0;
  const cxPendingCount = (pricingModeData?.conciergePendingManage?.aggregate?.count || 0) + (pricingModeData?.conciergePendingList?.aggregate?.count || 0);
  const cxPendingPotential = (pricingModeData?.conciergePendingManage?.aggregate?.sum?.tp_list_price || 0) + (pricingModeData?.conciergePendingList?.aggregate?.sum?.tp_list_price || 0);
  const allPendingPotentialFromDB = (pricingModeData?.allPendingManage?.aggregate?.sum?.tp_list_price || 0) + (pricingModeData?.allPendingList?.aggregate?.sum?.tp_list_price || 0);
  const spUserCount = userCountsData?.selfPricingUsers?.aggregate?.count || 0;
  const cxUserCount = userCountsData?.conciergeUsers?.aggregate?.count || 0;
  const untaggedUserCount = userCountsData?.untaggedUsers?.aggregate?.count || 0;

  const calculateUserPendingPotential = (tickets: Array<any> = []) => {
    return tickets.reduce((sum, ticket) => {
      const status = String(ticket?.t_status || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      if (status === 'tobeverified' || status === 'verified' || status === 'list' || status === 'listed') {
        return sum + (Number(ticket?.t_list_price) || 0);
      }
      return sum;
    }, 0);
  };

  const readAggregateListPriceSum = (...parts: Array<any>) =>
    parts.reduce((sum, part) => sum + Number(part?.aggregate?.sum?.tp_list_price ?? 0), 0);

  const resolvePotentialAmount = (
    managePart: any,
    listPart: any,
    pendingCount: number,
    listedCount: number,
    avgPrice: number,
  ) => {
    const fromDb = readAggregateListPriceSum(managePart, listPart);
    if (fromDb > 0) {
      return fromDb;
    }
    const aggPending = Number(managePart?.aggregate?.count ?? 0);
    const aggListed = Number(listPart?.aggregate?.count ?? 0);
    const aggCount = aggPending + aggListed;
    // DB returned tickets but sum is 0 — list prices are genuinely zero, do not estimate.
    if (aggCount > 0) {
      return 0;
    }
    const ticketCount = pendingCount + listedCount;
    return ticketCount > 0 && avgPrice > 0 ? ticketCount * avgPrice : 0;
  };

  const allTimeAvgTicketPriceFromPricing = (() => {
    const sp = pricingModeDataAll?.selfPricing?.aggregate;
    const cx = pricingModeDataAll?.concierge?.aggregate;
    const rev = Number(sp?.sum?.tp_sold_amount ?? 0) + Number(cx?.sum?.tp_sold_amount ?? 0);
    const sold = Number(sp?.count ?? 0) + Number(cx?.count ?? 0);
    return sold > 0 ? rev / sold : 0;
  })();

  const getSettledTicketsFromTicketList = (tickets: Array<any> = []) => {
    return tickets.reduce((count, ticket) => {
      const status = String(ticket?.t_status || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const paymentStatus = String(ticket?.t_payment_status || '').toLowerCase();
      return count + ((status === 'sold' && paymentStatus === 'success') ? 1 : 0);
    }, 0);
  };

  const selectedUserPendingPotential = selectedUserId
    ? ((pendingTickets?.potentialPayout ?? 0) || calculateUserPendingPotential(userTicketsInDashboardRange))
    : 0;

  const marketingSettledTickets = useMemo(() => {
    if (!isMarketingUser || !Array.isArray(marketingSoldData?.filterlisttickets)) return 0;
    const managedSet = new Set(managedUserIdsForDashboard);
    return marketingSoldData.filterlisttickets.reduce((count: number, ticket: any) => {
      if (!managedSet.has(ticket?.u_id)) return count;
      const status = String(ticket?.tp_status || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const paymentStatus = String(ticket?.tp_payment_status || '').toLowerCase();
      return count + ((status === 'sold' && paymentStatus === 'success') ? 1 : 0);
    }, 0);
  }, [isMarketingUser, managedUserIdsForDashboard, marketingSoldData]);
  const selectedUserSelfPricingPotential = selectedUserPricingMode === 'self-pricing' ? selectedUserPendingPotential : 0;
  const selectedUserConciergePotential = selectedUserPricingMode === 'concierge' ? selectedUserPendingPotential : 0;

  const marketingPendingByMode = useMemo(() => {
    if (!isMarketingUser) {
      return { selfPricing: 0, concierge: 0 };
    }
    const managedSet = new Set(managedUserIdsForDashboard);
    const verificationRows = (marketingVerificationData?.filtermanagetickets || []).filter((row: any) =>
      managedSet.has(row?.u_id)
    );
    const listedRows = (marketingListedData?.filterlisttickets || []).filter((row: any) =>
      managedSet.has(row?.u_id)
    );
    const pendingRows = [...verificationRows, ...listedRows];
    return pendingRows.reduce(
      (acc, row: any) => {
        const mode = String(row?.u_pricing_mode || "").toLowerCase().trim();
        const amount = Number(row?.tp_list_price || 0) || 0;
        if (mode === "self-pricing" || mode === "selfpricing" || mode === "self_pricing") {
          acc.selfPricing += amount;
        } else if (mode === "concierge") {
          acc.concierge += amount;
        }
        return acc;
      },
      { selfPricing: 0, concierge: 0 }
    );
  }, [isMarketingUser, managedUserIdsForDashboard, marketingVerificationData, marketingListedData]);

  let displayedSelfPricingPotential = isMarketingUser
    ? marketingPendingByMode.selfPricing
    : selectedUserId
      ? selectedUserSelfPricingPotential
      : spPendingPotential;
  let displayedConciergePotential = isMarketingUser
    ? marketingPendingByMode.concierge
    : selectedUserId
      ? selectedUserConciergePotential
      : cxPendingPotential;

  // Ticket status counts from pricingModeData (filtered by client type)
  const spToBeVerified = pricingModeData?.spToBeVerified?.aggregate?.count || 0;
  const spVerified = pricingModeData?.spVerified?.aggregate?.count || 0;
  const spListed = pricingModeData?.spListed?.aggregate?.count || 0;
  const spSoldTickets = pricingModeData?.spSoldTickets?.aggregate?.count || 0;
  const spSettled = pricingModeData?.spSettled?.aggregate?.count || 0;
  const spDelisted = pricingModeData?.spDelisted?.aggregate?.count || 0;
  const spUnsold = pricingModeData?.spUnsold?.aggregate?.count || 0;
  const cxToBeVerified = pricingModeData?.cxToBeVerified?.aggregate?.count || 0;
  const cxVerified = pricingModeData?.cxVerified?.aggregate?.count || 0;
  const cxListed = pricingModeData?.cxListed?.aggregate?.count || 0;
  const cxSoldTickets = pricingModeData?.cxSoldTickets?.aggregate?.count || 0;
  const cxSettled = pricingModeData?.cxSettled?.aggregate?.count || 0;
  const cxDelisted = pricingModeData?.cxDelisted?.aggregate?.count || 0;
  const cxUnsold = pricingModeData?.cxUnsold?.aggregate?.count || 0;

  // Ticket status from main query or user tickets
  let _verifiedTickets = 0;
  let _toBeVerifiedTickets = 0;
  let _listedTickets = 0;
  let _delistedTickets = 0;
  let _unsoldTickets = 0;
  let _realSoldTickets = 0;
  let _settledTickets = 0;

  if (selectedUserId && userTicketsData?.tickets) {
    const normalizeStatusToken = (status: any) => (status || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    userTicketsInDashboardRange.forEach((ticket: { t_status: any }) => {
      const s = normalizeStatusToken(ticket.t_status);
      if (s === 'verified') _verifiedTickets++;
      if (s === 'tobeverified') _toBeVerifiedTickets++;
      if (s === 'list' || s === 'listed') _listedTickets++;
    });

    userTicketsInDashboardRange.forEach((ticket: { t_status: any; t_payment_status?: any }) => {
      const s = normalizeStatusToken(ticket.t_status);
      if ([
        'delist', 'delistinprogress', 'delistrequested', 'listingrequestexpired', 'closed'
      ].includes(s)) _delistedTickets++;
      if (s === 'unsold' || s === 'return') _unsoldTickets++;
      if (s === 'sold') {
        _realSoldTickets++;
        if (String(ticket?.t_payment_status || '').toLowerCase() === 'success') {
          _settledTickets++;
        }
      }
    });
  } else if (isMarketingUser) {
    _settledTickets = marketingSettledTickets;
  } else {
    const activeTicketStatusData = ticketStatusData;
    _verifiedTickets = activeTicketStatusData?.verifiedCount?.aggregate?.count || 0;
    _toBeVerifiedTickets = activeTicketStatusData?.toBeVerifiedCount?.aggregate?.count || 0;
    _listedTickets = activeTicketStatusData?.listedCount?.aggregate?.count || 0;
    _delistedTickets = activeTicketStatusData?.delistedCount?.aggregate?.count || 0;
    _unsoldTickets = activeTicketStatusData?.unsoldCount?.aggregate?.count || 0;
    _realSoldTickets = activeTicketStatusData?.soldCount?.aggregate?.count || 0;
    _settledTickets = activeTicketStatusData?.settledCount?.aggregate?.count || 0;
  }

  // Apply client type filters - FIXED: Determine which data to show based on filters
  const isModeFiltered = !selectedUserId && selectedSellerMode !== 'all';
  const isUserSelected = !!selectedUserId;

  let activeLeagueTimingData: any = null;
  let activeMarketplaceSoldData: any = null;

  let totalRevenue = baseTotalRevenue;
  let qaProfit = baseQaProfit;
  let vlFee = baseVlFee;
  let userPayout = baseUserPayout;
  let totalTickets = baseTotalTickets;
  let soldTickets = baseSoldTickets;
  let pendingFromLeagues = basePendingFromLeagues;
  // potentialPayout: use selected user pending total when user is selected; otherwise use DB-wide pending totals
  let potentialPayout = isUserSelected
    ? selectedUserPendingPotential
    : (allPendingPotentialFromDB > 0 ? allPendingPotentialFromDB : 0);
  let toBeVerifiedTickets = _toBeVerifiedTickets;
  let verifiedTickets = _verifiedTickets;
  let listedTickets = _listedTickets;
  let delistedTickets = _delistedTickets;
  let unsoldTickets = _unsoldTickets;
  let realSoldTickets = _realSoldTickets;
  let settledTickets = _settledTickets;
  let totalPendingTickets = _toBeVerifiedTickets + _verifiedTickets;
  let avgTicketPrice = baseAvgTicketPrice;

  // Apply self-pricing or concierge filter when no user is selected
  if (isModeFiltered) {
    if (selectedSellerMode === 'self-pricing') {
      console.group('[AdminDashboard] 🟦 Applying Self-Pricing filter');
      console.log('📊 Revenue Data:', { spRevenue, spProfit, spSoldCount });
      console.log('🎫 Ticket Status:', { spToBeVerified, spVerified, spListed });
      console.log('📤 Settlement:', { spSoldTickets, spDelisted, spUnsold });
      console.log('💰 Potential:', { spPendingPotential, spPendingCount });
      console.log('⚙️ Filter State:', { isModeFiltered, selectedSellerMode, effectiveClientType });
      console.groupEnd();
      totalRevenue = spRevenue;
      qaProfit = spProfit;
      soldTickets = spSoldCount;
      totalPendingTickets = spToBeVerified + spVerified;
      toBeVerifiedTickets = spToBeVerified;
      verifiedTickets = spVerified;
      listedTickets = spListed;
      delistedTickets = spDelisted;
      unsoldTickets = spUnsold;
      realSoldTickets = spSoldTickets;
      settledTickets = cxSettled;
      potentialPayout = spPendingPotential;
      vlFee = 0;
      userPayout = Math.max(0, totalRevenue - qaProfit);
      avgTicketPrice = realSoldTickets > 0 ? totalRevenue / realSoldTickets : 0;
      console.log('✅ SP Filter Applied - Revenue:', totalRevenue, 'Sold:', realSoldTickets, 'Payout:', userPayout);

      activeLeagueTimingData = clientTypeLeagueRaw;
      activeMarketplaceSoldData = clientTypeMarketplaceRaw;
      console.log('🗺️ SP Data Sources Set:', { hasLeagueData: !!activeLeagueTimingData, hasMarketplaceData: !!activeMarketplaceSoldData });
    } else if (selectedSellerMode === 'concierge') {
      console.group('[AdminDashboard] 🟪 Applying Concierge filter');
      console.log('📊 Revenue Data:', { cxRevenue, cxProfit, cxSoldCount });
      console.log('🎫 Ticket Status:', { cxToBeVerified, cxVerified, cxListed });
      console.log('📤 Settlement:', { cxSoldTickets, cxDelisted, cxUnsold });
      console.log('💰 Potential:', { cxPendingPotential, cxPendingCount });
      console.groupEnd();
      
      totalRevenue = cxRevenue;
      qaProfit = cxProfit;
      soldTickets = cxSoldCount;
      totalPendingTickets = cxToBeVerified + cxVerified;
      toBeVerifiedTickets = cxToBeVerified;
      verifiedTickets = cxVerified;
      listedTickets = cxListed;
      delistedTickets = cxDelisted;
      unsoldTickets = cxUnsold;
      realSoldTickets = cxSoldTickets;
      settledTickets = cxSettled;
      potentialPayout = cxPendingPotential;
      vlFee = 0;
      userPayout = Math.max(0, totalRevenue - qaProfit);
      avgTicketPrice = realSoldTickets > 0 ? totalRevenue / realSoldTickets : 0;
      console.log('✅ CX Filter Applied - Revenue:', totalRevenue, 'Sold:', realSoldTickets, 'Payout:', userPayout);

      activeLeagueTimingData = clientTypeLeagueRaw;
      activeMarketplaceSoldData = clientTypeMarketplaceRaw;
      console.log('🗺️ CX Data Sources Set:', { hasLeagueData: !!activeLeagueTimingData, hasMarketplaceData: !!activeMarketplaceSoldData });
    }
  }

  // When user is selected, we use their specific data
  if (isUserSelected) {
    avgTicketPrice = realSoldTickets > 0 ? totalRevenue / realSoldTickets : baseAvgTicketPrice;
    console.log('[AdminDashboard] 👤 User-Specific Data Applied:', {
      userId: selectedUserId,
      userName: selectedUserName,
      userClientType: selectedUserClientType,
      revenue: totalRevenue,
      soldTickets: realSoldTickets,
      avgPrice: avgTicketPrice
    });
  }

  // Ensure when a specific user is selected the financial metrics are taken from the
  // per-user revenue aggregate (if available). This prevents the dashboard from
  // showing global totals when a user filter is active.
  if (isUserSelected) {
    // Prefer Hasura-provided per-user aggregate when available
    const userAgg = userRevenueData?.getRevenueForSuperUserAggregate?.[0];

    // Compute fallback sums from per-user sold rows (use sale-date filtered rows when dashboard date filter active)
    const perUserSoldRows = (userMarketplaceSoldData?.soldByMarketplace || []) as any[];
    const perUserSoldFiltered = filterTicketsByDashboardEventDate(perUserSoldRows, dashboardDateFilterActive, dashboardDateRange);
    const computedUser = perUserSoldFiltered.reduce((acc, r) => {
      acc.revenue += Number(r.tp_sold_amount || 0);
      acc.profit += Number(r.tp_quick_cut_amount || 0);
      acc.count += 1;
      return acc;
    }, { revenue: 0, profit: 0, count: 0 });

    if (userAgg) {
      totalRevenue = Number(userAgg.overAllSales || 0);
      qaProfit = Number(userAgg.quickasystFee || 0);
      if (Number(userAgg.overAllTickets || 0) > 0) totalTickets = Number(userAgg.overAllTickets || 0);
    } else if (computedUser.count > 0) {
      totalRevenue = computedUser.revenue;
      qaProfit = computedUser.profit;
      totalTickets = computedUser.count;
    } else if (userDatedSoldMetrics) {
      totalRevenue = userDatedSoldMetrics.revenue;
      qaProfit = userDatedSoldMetrics.profit;
      totalTickets = userDatedSoldMetrics.count || totalTickets;
    }

    // Recompute dependent values
    userPayout = Math.max(0, totalRevenue - (Number(qaProfit) || 0) - vlFee);
    avgTicketPrice = realSoldTickets > 0 ? totalRevenue / realSoldTickets : (totalTickets > 0 ? totalRevenue / totalTickets : avgTicketPrice);

    console.log('[AdminDashboard][USER OVERRIDE] totalRevenue, qaProfit, userPayout, totalTickets:', totalRevenue, qaProfit, userPayout, totalTickets);
  }

  // League breakdown - FIXED: Use correct data source
  if (isUserSelected) {
    activeLeagueTimingData = userLeagueTimingData;
  } else if (isModeFiltered && effectiveClientType) {
    activeLeagueTimingData = clientTypeLeagueRaw;
  } else {
    activeLeagueTimingData = leagueTimingData;
  }

 // ✅ KEEP THIS - NEW CODE
const rawLeagueSold = (() => {
  let data = [];
  if (isModeFiltered && clientTypeLeagueRaw?.soldByLeagueRaw) {
    data = clientTypeLeagueRaw.soldByLeagueRaw.map((row: any) => ({
      l_name: row.ticket?.event?.event_categories?.[0]?.league_category?.league?.l_name || null,
      // tp_sold_at: row.tp_sold_at,
      tp_sold_amount: row.tp_sold_amount,
      tp_quick_cut_amount: row.tp_quick_cut_amount,
    }));
  } else {
    data = activeLeagueTimingData?.soldByLeague || [];
  }
  return data;
})();

const rawLeagueSoldInRange = filterTicketsByDashboardEventDate(
  rawLeagueSold,
  dashboardDateFilterActive,
  dashboardDateRange,
);



  // --- MODIFIED: Build leagueSoldMap starting with all leagues (zero-initialized) ---
  const leagueSoldMap = new Map<string, LeagueSoldBreakdown>();
  // First, add every league from allLeagues (so even leagues with zero sales appear)
  allLeagues.forEach(leagueName => {
    leagueSoldMap.set(leagueName, {
      leagueName,
      soldCount: 0,
      revenue: 0,
      profit: 0,
    });
  });
  // Then overlay actual sold records
  rawLeagueSoldInRange.forEach((row: any) => {
    const name = row.l_name || 'Unknown';
    const existing = leagueSoldMap.get(name) ?? {
      leagueName: name,
      soldCount: 0,
      revenue: 0,
      profit: 0,
    };
    leagueSoldMap.set(name, {
      leagueName: name,
      soldCount: existing.soldCount + 1,
      revenue: existing.revenue + Number(row.tp_sold_amount || 0),
      profit: existing.profit + Number(row.tp_quick_cut_amount || 0),
    });
  });
  const leagueBreakdown: LeagueSoldBreakdown[] = Array.from(leagueSoldMap.values()).sort((a, b) => b.revenue - a.revenue);
  // --------------------------------------------------------------------------------

  console.group('[AdminDashboard] 🏆 League Breakdown Aggregated');
  console.log('Raw records processed:', rawLeagueSoldInRange.length);
  console.log('Unique leagues (including zero-sale):', leagueSoldMap.size);
  console.log('Top 5 leagues by revenue:', leagueBreakdown.slice(0, 5));
  console.groupEnd();

  // Marketplace breakdown - FIXED: Use correct data source
  if (isUserSelected) {
    activeMarketplaceSoldData = userMarketplaceSoldData;
  } else if (isModeFiltered && effectiveClientType) {
    activeMarketplaceSoldData = clientTypeMarketplaceRaw;
  } else {
    activeMarketplaceSoldData = marketplaceSoldData;
  }

const rawMarketplaceSoldAll = activeMarketplaceSoldData?.soldByMarketplace || [];
// ← USE SALE DATE for financial data filtering
const rawMarketplaceSold = filterTicketsByDashboardEventDate(
  rawMarketplaceSoldAll,
  dashboardDateFilterActive,
  dashboardDateRange,
);
  
  const mpMap = new Map<string, { soldCount: number; revenue: number; profit: number; vlFeeAmount: number }>();
  rawMarketplaceSold.forEach((row: any) => {
    const name = getMarketplaceName(row.tp_marketplace_name);
    if (!name || name === 'Unknown') return;

    const normalizedName = normalizeMarketplaceName(name);
    const marketplace = marketplaceFeeLookup.get(normalizedName);
    const feeRate = marketplace?.vl_fee_percentage ?? 0;
    const revenue = Number(row.tp_sold_amount || 0);
    const profit = Number(row.tp_quick_cut_amount || 0);
    const statedVlFeeAmount = Number(row.tp_vl_fee ?? 0);
    const vlFeeAmount = statedVlFeeAmount > 0 ? statedVlFeeAmount : revenue * (feeRate / 100);
    const existing = mpMap.get(name) ?? {
      soldCount: 0,
      revenue: 0,
      profit: 0,
      vlFeeAmount: 0,
    };

    mpMap.set(name, {
      soldCount: existing.soldCount + 1,
      revenue: existing.revenue + revenue,
      profit: existing.profit + profit,
      vlFeeAmount: existing.vlFeeAmount + vlFeeAmount,
    });
  });
  console.log('Unique marketplaces with sales:', mpMap.size);
  console.log('Marketplace map:', Object.fromEntries(mpMap));

  const knownMarketplaceNames = new Set(marketplaces.map(mp => mp.name.toLowerCase()));
  const unknownMarketplaceBreakdown: MarketplaceSoldBreakdown[] = Array.from(mpMap.entries())
    .filter(([name]) => !knownMarketplaceNames.has(name.toLowerCase()))
    .map(([name, data]) => ({
      name,
      soldCount: data.soldCount,
      revenue: data.revenue,
      profit: data.profit,
      vlFeeRate: data.revenue > 0 ? (data.vlFeeAmount / data.revenue) * 100 : 0,
      vlFeeAmount: data.vlFeeAmount,
    }));

  const marketplaceBreakdown: MarketplaceSoldBreakdown[] = [
    ...marketplaces.map(mp => {
      const data = mpMap.get(mp.name) || { soldCount: 0, revenue: 0, profit: 0, vlFeeAmount: 0 };
      const vlFeeAmount = data.vlFeeAmount;
      const vlFeeRate = data.revenue > 0 ? (vlFeeAmount / data.revenue) * 100 : mp.vl_fee_percentage;
      return {
        name: mp.name,
        soldCount: data.soldCount,
        revenue: data.revenue,
        profit: data.profit,
        vlFeeRate,
        vlFeeAmount,
      };
    }),
    ...unknownMarketplaceBreakdown,
  ].sort((a, b) => b.revenue - a.revenue);
  console.log('Marketplace breakdown (sorted):', marketplaceBreakdown);
  console.table(marketplaceBreakdown.slice(0, 5));
  console.groupEnd();

  const totalMarketplaceRevenue = marketplaceBreakdown.reduce((sum, mp) => sum + mp.revenue, 0);
  const weightedVlFeeRate = totalMarketplaceRevenue > 0
    ? marketplaceBreakdown.reduce((sum, mp) => sum + mp.revenue * (mp.vlFeeRate / 100), 0) / totalMarketplaceRevenue
    : 0;

  const vlFeeByMarketplaceSorted = [...marketplaceBreakdown]
    .filter((mp) => mp.soldCount > 0)
    .sort((a, b) => b.vlFeeAmount - a.vlFeeAmount);
  const totalVlFeeFromMarketplaces = marketplaceBreakdown.reduce((sum, mp) => sum + mp.vlFeeAmount, 0);

  const qaFeeByMarketplaceSorted = useMemo(
    () => [...marketplaceBreakdown].filter((mp) => mp.soldCount > 0).sort((a, b) => b.profit - a.profit),
    [marketplaceBreakdown],
  );
  const totalQaFeeFromMarketplaces = useMemo(
    () => marketplaceBreakdown.reduce((sum, mp) => sum + mp.profit, 0),
    [marketplaceBreakdown],
  );

  if (totalVlFeeFromMarketplaces > 0) {
    vlFee = totalVlFeeFromMarketplaces;
  } else {
    vlFee = 0;
  }
  userPayout = Math.max(0, totalRevenue - qaProfit - vlFee);

  const revenueForVlFeeDisplay = qaProfit + userPayout + vlFee;
  const displayedVlFeeRate = revenueForVlFeeDisplay > 0
    ? vlFee / revenueForVlFeeDisplay
    : (totalRevenue > 0 ? vlFee / totalRevenue : weightedVlFeeRate);

 
  if (totalRevenue > 0 && realSoldTickets > 0) {
    avgTicketPrice = totalRevenue / realSoldTickets;
  } else if (avgTicketPrice === 0 && totalRevenue > 0) {
   
    const soldDenominator = realSoldTickets > 0
      ? realSoldTickets
      : (!isModeFiltered && !isUserSelected && _fallbackSoldCount > 0 ? _fallbackSoldCount : 0);
    if (soldDenominator > 0) {
      avgTicketPrice = totalRevenue / soldDenominator;
    }
  }

  const avgForPotential = (() => {
    if (avgTicketPrice > 0) return avgTicketPrice;
    if (baseAvgTicketPrice > 0) return baseAvgTicketPrice;
    if (totalRevenue > 0 && realSoldTickets > 0) return totalRevenue / realSoldTickets;
    if (totalRevenue > 0 && _fallbackSoldCount > 0) return totalRevenue / _fallbackSoldCount;
    return allTimeAvgTicketPriceFromPricing;
  })();

  if (!isMarketingUser) {
    if (!isUserSelected) {
      if (potentialPayout === 0) {
        if (isModeFiltered && selectedSellerMode === 'self-pricing') {
          potentialPayout = resolvePotentialAmount(
            pricingModeData?.selfPricingPendingManage,
            pricingModeData?.selfPricingPendingList,
            spToBeVerified + spVerified,
            spListed,
            avgForPotential,
          );
        } else if (isModeFiltered && selectedSellerMode === 'concierge') {
          potentialPayout = resolvePotentialAmount(
            pricingModeData?.conciergePendingManage,
            pricingModeData?.conciergePendingList,
            cxToBeVerified + cxVerified,
            cxListed,
            avgForPotential,
          );
        } else {
          potentialPayout = resolvePotentialAmount(
            pricingModeData?.allPendingManage,
            pricingModeData?.allPendingList,
            totalPendingTickets,
            listedTickets,
            avgForPotential,
          );
        }
      }
      if (displayedSelfPricingPotential === 0 && !(isModeFiltered && selectedSellerMode === 'concierge')) {
        displayedSelfPricingPotential = resolvePotentialAmount(
          pricingModeData?.selfPricingPendingManage,
          pricingModeData?.selfPricingPendingList,
          spToBeVerified + spVerified,
          spListed,
          avgForPotential,
        );
      }
      if (displayedConciergePotential === 0 && !(isModeFiltered && selectedSellerMode === 'self-pricing')) {
        displayedConciergePotential = resolvePotentialAmount(
          pricingModeData?.conciergePendingManage,
          pricingModeData?.conciergePendingList,
          cxToBeVerified + cxVerified,
          cxListed,
          avgForPotential,
        );
      }
      if (!isModeFiltered) {
        const combinedModePotential = displayedSelfPricingPotential + displayedConciergePotential;
        if (combinedModePotential > 0) {
          potentialPayout = combinedModePotential;
        }
      } else if (selectedSellerMode === 'self-pricing') {
        displayedConciergePotential = 0;
        if (potentialPayout > 0) {
          displayedSelfPricingPotential = potentialPayout;
        } else if (displayedSelfPricingPotential > 0) {
          potentialPayout = displayedSelfPricingPotential;
        }
      } else if (selectedSellerMode === 'concierge') {
        displayedSelfPricingPotential = 0;
        if (potentialPayout > 0) {
          displayedConciergePotential = potentialPayout;
        } else if (displayedConciergePotential > 0) {
          potentialPayout = displayedConciergePotential;
        }
      }
    } else if (selectedUserPendingPotential === 0 && (totalPendingTickets + listedTickets) > 0 && avgForPotential > 0) {
      const userPotential = (totalPendingTickets + listedTickets) * avgForPotential;
      if (selectedUserPricingMode === 'self-pricing') {
        displayedSelfPricingPotential = userPotential;
      } else if (selectedUserPricingMode === 'concierge') {
        displayedConciergePotential = userPotential;
      }
      potentialPayout = userPotential;
    }
  }

  console.log('[AdminDashboard] 💵 VL Fee Summary:', { 
    totalVlFeeFromMarketplaces, 
    avgVlFeeRate: `${(displayedVlFeeRate * 100).toFixed(2)}%`,
    topMarketplace: vlFeeByMarketplaceSorted[0]?.name,
    topMarketplaceFee: vlFeeByMarketplaceSorted[0]?.vlFeeAmount
  });
  console.log('[AdminDashboard] 💰 QA Marketplace Fee Summary:', {
    totalQaFeeFromMarketplaces,
    topMarketplace: qaFeeByMarketplaceSorted[0]?.name,
    topMarketplaceQaFee: qaFeeByMarketplaceSorted[0]?.profit,
  });

  // List timing data
  const rawListTiming = (() => {
    if (isModeFiltered && clientTypeLeagueRaw?.listedForTimingRaw) {
      return clientTypeLeagueRaw.listedForTimingRaw.map((row: any) => ({
        tp_created_at: row.tp_created_at,
        e_date: row.ticket?.event?.e_date,
      }));
    }
    return activeLeagueTimingData?.listedForTiming || [];
  })();

  const rawListTimingInRange = filterTicketsByDashboardEventDate(
    rawListTiming,
    dashboardDateFilterActive,
    dashboardDateRange,
  );

  const listDaysBefore = rawListTimingInRange
    .filter((row: any) => row.e_date && row.tp_created_at)
    .map((row: any) => (new Date(row.e_date).getTime() - new Date(row.tp_created_at).getTime()) / 86400000)
    .filter((d: number) => d >= 0 && d < 365);
  const listTimingTotal = listDaysBefore.length;
  const listTimingAvg = listTimingTotal > 0 ? listDaysBefore.reduce((s: number, d: number) => s + d, 0) / listTimingTotal : 0;
  
  const listTimingBucketDefs = [
    { label: '<24 hrs', min: 0, max: 1 }, { label: '1-2 days', min: 1, max: 2 },
    { label: '2-3 days', min: 2, max: 3 }, { label: '3-5 days', min: 3, max: 5 },
    { label: '5-7 days', min: 5, max: 7 }, { label: '7-14 days', min: 7, max: 14 },
    { label: '14+ days', min: 14, max: 999 },
  ];
  const listTimingBuckets = listTimingBucketDefs.map(b => ({
    label: b.label,
    count: listDaysBefore.filter((d: number) => d >= b.min && d < b.max).length,
  }));
  const listTimingMax = Math.max(...listTimingBuckets.map(b => b.count), 1);

  const normalizeMode = (value: any) => String(value || "").toLowerCase().replace(/[\s_]/g, "-");
  const activeMode = selectedSellerMode === "all" ? "all" : normalizeMode(selectedSellerMode);
  const managedUserSet = new Set(managedUserIdsForDashboard);
  const delistTimingRows = (marketingDelistedData?.filterreturntickets || []).filter((row: any) => {
    if (isMarketingUser && managedUserSet.size > 0 && !managedUserSet.has(row?.u_id)) return false;
    if (selectedUserId && row?.u_id !== selectedUserId) return false;
    if (activeMode !== "all" && normalizeMode(row?.u_pricing_mode) !== activeMode) return false;
    return true;
  });

  const delistDaysBefore = delistTimingRows
    .filter((row: any) => row?.e_date && row?.tp_updated_at)
    .map((row: any) => (new Date(row.e_date).getTime() - new Date(row.tp_updated_at).getTime()) / 86400000)
    .filter((d: number) => Number.isFinite(d) && d >= 0 && d < 365);

  const delistTimingTotal = delistDaysBefore.length;
  const delistTimingBuckets = listTimingBucketDefs.map((b) => ({
    label: b.label,
    count: delistDaysBefore.filter((d: number) => d >= b.min && d < b.max).length,
  }));
  const delistTimingMax = Math.max(...delistTimingBuckets.map((b) => b.count), 1);
  const blockedDelistAttempts = delistDaysBefore.filter((d: number) => d <= (3 / 24)).length;

  // Signup funnel
  const sfStarted = signupFunnelData?.started?.aggregate?.count || 0;
  const sfPersonal = signupFunnelData?.submittedInfo?.aggregate?.count || sfStarted;
  const sfAccountType = signupFunnelData?.selectedAccountType?.aggregate?.count || 0;
  const sfPricing = signupFunnelData?.selectedPricing?.aggregate?.count || 0;
  const sfCompleted = signupFunnelData?.completedSignup?.aggregate?.count || 0;
  const sfCompletionRate = sfStarted > 0 ? Math.round((sfCompleted / sfStarted) * 100) : 0;

  const signupSteps = [
    { label: 'Started Signup', count: sfStarted },
    { label: 'Submitted Personal Info', count: sfPersonal },
    { label: 'Selected Account Type', count: sfAccountType },
    { label: 'Selected Pricing Option', count: sfPricing },
    { label: 'Completed Signup', count: sfCompleted },
  ];
  const signupStepsMax = Math.max(...signupSteps.map(s => s.count), 1);

  const criticalMetricsLoading =
    pendingLoading ||
    revenueLoading ||
    topSalesLoading ||
    userRevenueLoading ||
    ticketStatusMetricsLoading ||
    pricingModeMetricsLoading ||
    userTicketsLoading ||
    salesRepByEmailLoading ||
    managedUsersByRepLoading ||
    marketingVerificationLoading ||
    marketingListedLoading ||
    marketingSoldLoading ||
    marketingDelistedLoading ||
    marketingUnsoldLoading;

  const secondaryMetricsLoading =
    marketplacesLoading ||
    leagueTimingLoading ||
    userLeagueTimingLoading ||
    marketplaceSoldLoading ||
    userMarketplaceSoldLoading ||
    signupFunnelLoading ||
    clientTypeLeagueLoading ||
    clientTypeMarketplaceLoading ||
    leaguesListLoading;

  const isLoading = criticalMetricsLoading || isRefreshing;

  if (isMarketingUser) {
    const managedUsers = managedUsersByRepData?.managedUsers || [];
    const verificationRows = (marketingVerificationData?.filtermanagetickets || []).filter((row: any) => managedUserSet.has(row?.u_id));
    const listedRows = (marketingListedData?.filterlisttickets || []).filter((row: any) => managedUserSet.has(row?.u_id));
    const soldRows = (marketingSoldData?.FilterSoldTickets || []).filter((row: any) => managedUserSet.has(row?.u_id));
    const delistedRows = (marketingDelistedData?.filterreturntickets || []).filter((row: any) => managedUserSet.has(row?.u_id));
    const unsoldRows = (marketingUnsoldData?.filterUnsoldTickets || []).filter((row: any) => managedUserSet.has(row?.u_id));

    const bySelectedMode = (row: any) =>
      activeMode === "all" || normalizeMode(row?.u_pricing_mode) === activeMode;

    const verificationRowsByMode = verificationRows.filter(bySelectedMode);
    const listedRowsByMode = listedRows.filter(bySelectedMode);
    const soldRowsByMode = soldRows.filter(bySelectedMode);
    const delistedRowsByMode = delistedRows.filter(bySelectedMode);
    const unsoldRowsByMode = unsoldRows.filter(bySelectedMode);

    const marketingRevenue = managedUsers.reduce((sum, user) => sum + (Number(user?.tickets_sold_amount || 0) || 0), 0);
    const marketingLifetime = managedUsers.reduce((sum, user) => sum + (Number(user?.u_total_earnings || 0) || 0), 0);
    const marketingCommission = marketingRevenue * (salesRepCommissionRate / 100);
    const marketingRevenueByMode = soldRowsByMode.reduce((sum: number, row: any) => sum + (Number(row?.tp_sold_amount || 0) || 0), 0);
    const marketingProfitByMode = soldRowsByMode.reduce((sum: number, row: any) => sum + (Number(row?.tp_quick_cut_amount || 0) || 0), 0);
    const marketingPotentialByMode =
      [...verificationRowsByMode, ...listedRowsByMode].reduce((sum: number, row: any) => sum + (Number(row?.tp_list_price || 0) || 0), 0);

    totalRevenue = activeMode === "all" ? marketingRevenue : marketingRevenueByMode;
    qaProfit = activeMode === "all" ? (marketingCommission || marketingLifetime) : marketingProfitByMode;
    vlFee = 0;
    userPayout = activeMode === "all"
      ? (marketingCommission || marketingLifetime)
      : Math.max(0, totalRevenue - qaProfit);
    totalTickets = managedUsers.length;
    realSoldTickets = activeMode === "all" ? managedUsers.length : soldRowsByMode.length;
    soldTickets = realSoldTickets;
    const repToBeVerified = verificationRowsByMode.filter((row: any) => row?.tp_status === "ToBeVerified").length;
    const repVerified = verificationRowsByMode.filter((row: any) => row?.tp_status === "Verified").length;
    const repListed = listedRowsByMode.length;
    const repSold = soldRowsByMode.length;
    const repDelisted = delistedRowsByMode.length;
    const repUnsold = unsoldRowsByMode.length;
    totalPendingTickets = repToBeVerified + repVerified;
    toBeVerifiedTickets = repToBeVerified;
    verifiedTickets = repVerified;
    listedTickets = repListed;
    realSoldTickets = repSold;
    soldTickets = repSold;
    delistedTickets = repDelisted;
    unsoldTickets = repUnsold;
    avgTicketPrice = realSoldTickets > 0 ? totalRevenue / realSoldTickets : 0;
    potentialPayout = activeMode === "all" ? (marketingCommission || marketingLifetime) : marketingPotentialByMode;
  }
  
  useEffect(() => {
    console.group('[AdminDashboard] 📋 Final Computed State');
    console.log('🔍 Filter Summary:', {
      isModeFiltered,
      isUserSelected,
      selectedSellerMode,
      selectedUserId,
      effectiveClientType
    });
    console.log('💰 Revenue Summary:', {
      totalRevenue,
      qaProfit,
      vlFee: `$${formatCurrency(vlFee)}`,
      userPayout: `$${formatCurrency(userPayout)}`
    });
    console.log('🎫 Ticket Summary:', {
      totalPendingTickets,
      listedTickets,
      realSoldTickets,
      delistedTickets,
      unsoldTickets
    });
    console.log('📊 Breakdown Summary:', {
      topLeague: leagueBreakdown[0]?.leagueName,
      topLeagueRevenue: leagueBreakdown[0]?.revenue,
      totalLeagues: leagueBreakdown.length,
      topMarketplace: marketplaceBreakdown[0]?.name,
      topMarketplaceRevenue: marketplaceBreakdown[0]?.revenue,
      totalMarketplaces: marketplaceBreakdown.length
    });
    console.log('⚙️ Loading States:', {
      isLoading,
      criticalMetricsLoading,
      secondaryMetricsLoading,
      clientTypeLeagueLoading,
      clientTypeMarketplaceLoading,
      pricingModeMetricsLoading,
    });
    console.groupEnd();
  }, [totalRevenue, leagueBreakdown, marketplaceBreakdown, isModeFiltered, isUserSelected]);

  return (
    <div className={isDarkMode ? `${styles.dashboard} darkMode` : styles.dashboard}>
      {/* HEADER */}
      <div className={styles.header}>
        <div className={styles.headerLeftRow}>
          <div className={styles.headerTitleBlock}>
            <p className={styles.subtitle}>QuickAsyst Admin</p>
            <h1 className={styles.title}>Sell Dashboard</h1>
          </div>
          <div className={styles.searchAndIcons}>
            <div ref={searchRef} className={styles.searchWrapper}>
              <div className={styles.searchInputContainer}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search user by name or email..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => { if (searchQuery.trim()) setShowSearchResults(true); }}
                  className={styles.searchInput}
                  autoComplete="off"
                  aria-label="Search users"
                />
                {searchQuery && (
                  <button
                    className={styles.clearSearchBtn}
                    onClick={() => { setSearchQuery(""); setSearchResults([]); setShowSearchResults(false); }}
                    aria-label="Clear search"
                  >×</button>
                )}
              </div>
              {showSearchResults && (
                <div className={styles.searchResultsDropdown}>
                  {searchLoading && <div className={styles.searchResultMore}>Searching...</div>}
                  {!searchLoading && searchResults.length > 0 && searchResults.map((user) => (
                    <div key={user.u_id} className={styles.searchResultItem} onClick={() => handleUserSelect(user)}>
                      <div className={styles.searchResultAvatar}>
                        {(user.u_full_name || "?")[0].toUpperCase()}
                      </div>
                      <div className={styles.searchResultInfo}>
                        <div className={styles.searchResultName}>{user.u_full_name}</div>
                        <div className={styles.searchResultEmail}>{user.u_email_id}</div>
                        {user.u_pricing_mode && (
                          <span style={{
                            display: 'inline-block', marginTop: '2px', padding: '1px 8px',
                            borderRadius: '12px', fontSize: '10px', fontWeight: 600,
                            background: user.u_pricing_mode === 'self-pricing' ? '#dbeafe' : '#f3e8ff',
                            color: user.u_pricing_mode === 'self-pricing' ? '#1d4ed8' : '#7c3aed',
                          }}>
                            {formatClientType(user.u_pricing_mode)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {!searchLoading && searchResults.length === 0 && searchQuery.trim() !== "" && (
                    <div className={styles.searchResultMore}>No users found</div>
                  )}
                </div>
              )}
            </div>
            <div className={styles.iconGroup}>
              <div ref={dashboardDateRef} className={styles.dashboardDateFilterWrapper}>
                <button
                  type="button"
                  className={`${styles.iconBtn} ${dashboardDateFilterActive ? styles.dashboardDateBtnActive : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDashboardDatePicker((open) => !open);
                  }}
                  aria-label="Filter dashboard by date range"
                  title={dashboardDateFilterActive ? `Filtered: ${dashboardDateLabel}` : 'Filter by date range'}
                >
                  <Calendar size={18} />
                </button>
                {showDashboardDatePicker && (
                  <div className={styles.dashboardDateDropdown}>
                    <div className={styles.dashboardDatePresetList}>
                      {DASHBOARD_DATE_PRESETS.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          className={`${styles.dashboardDatePresetBtn} ${dashboardDatePeriod === preset ? styles.dashboardDatePresetBtnActive : ''}`}
                          onClick={() => handleDashboardDatePreset(preset)}
                        >
                          {preset === 'All' ? 'All time' : preset}
                        </button>
                      ))}
                    </div>
                    {dashboardDatePeriod === 'Custom' && (
                      <div className={styles.dashboardDateCustomFields}>
                        <label>
                          Start
                          <input
                            type="date"
                            value={customDashboardStart}
                            onChange={(e) => setCustomDashboardStart(e.target.value)}
                          />
                        </label>
                        <label>
                          End
                          <input
                            type="date"
                            value={customDashboardEnd}
                            onChange={(e) => setCustomDashboardEnd(e.target.value)}
                          />
                        </label>
                        <button
                          type="button"
                          className={styles.dashboardDatePresetBtn}
                          onClick={() => setShowDashboardDatePicker(false)}
                        >
                          Apply
                        </button>
                      </div>
                    )}
                    {dashboardDateFilterActive && (
                      <div className={styles.dashboardDateClearRow}>
                        <button
                          type="button"
                          className={styles.dashboardDateClearLink}
                          onClick={handleClearDashboardDateFilter}
                        >
                          Clear filter
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button 
                className={styles.iconBtn} 
                onClick={handleRefresh} 
                disabled={isLoading}
                aria-label="Refresh dashboard data"
                title="Refresh data"
              >
                <RefreshRoundedIcon className={isLoading ? styles.spinning : ''} />
              </button>
              <button 
                className={styles.iconBtn} 
                onClick={() => setIsDarkMode(!isDarkMode)}
                aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                title={isDarkMode ? "Light Mode" : "Dark Mode"}
              >
                {isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
              </button>
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.filtersRow}>
            <div className={styles.modeFilters}>
              <button
                className={`${styles.modeFilterBtn} ${selectedSellerMode === "all" ? styles.modeFilterBtnActive : ""}`}
                onClick={() => {
                  console.log('[AdminDashboard] 🔘 Clicked: All Filter');
                  setSelectedSellerMode("all");
                  handleClearUser();
                }}
                title="Show all data without filtering"
              >
                All
              </button>
              <button
                className={`${styles.modeFilterBtn} ${selectedSellerMode === "self-pricing" ? styles.modeFilterBtnActive : ""}`}
                onClick={() => {
                  console.log('[AdminDashboard] 🔘 Clicked: Self-Pricing Filter');
                  setSelectedSellerMode("self-pricing");
                  handleClearUser();
                }}
                title="Show only Self-Pricing data"
              >
                Self Pricing
              </button>
              <button
                className={`${styles.modeFilterBtn} ${selectedSellerMode === "concierge" ? styles.modeFilterBtnActive : ""}`}
                onClick={() => {
                  console.log('[AdminDashboard] 🔘 Clicked: Concierge Filter');
                  setSelectedSellerMode("concierge");
                  handleClearUser();
                }}
                title="Show only Concierge data"
              >
                Concierge
              </button>
            </div>
          </div>
        </div>
      </div>

      {dashboardDateFilterActive && (
        <div className={styles.dashboardDateBanner}>
          <strong>{dashboardDateLabel}</strong>
        
          <button
            type="button"
            className={styles.dashboardDateClearBtn}
            onClick={handleClearDashboardDateFilter}
            aria-label="Clear dashboard date filter"
          >
            Clear
          </button>
        </div>
      )}

      {/* Selected User Badge */}
      {selectedUserName && (
        <div className={styles.selectedUserBadgeContainer}>
          <div className={styles.selectedUserBadge}>
            <span className={styles.selectedUserIcon}>👤</span>
            <span>Viewing: <strong>{selectedUserName}</strong></span>
            {selectedUserClientType && (
              <span className={styles.selectedUserType} style={{
                marginLeft: '8px', padding: '2px 10px', borderRadius: '20px',
                fontSize: '11px', fontWeight: 600,
                background: selectedUserClientType === 'self-pricing' ? '#dbeafe' : '#f3e8ff',
                color: selectedUserClientType === 'self-pricing' ? '#1d4ed8' : '#7c3aed',
              }}>
                {formatClientType(selectedUserClientType)}
              </span>
            )}
            <button className={styles.clearUserBtn} onClick={handleClearUser} aria-label="Clear user filter">✕</button>
          </div>
        </div>
      )}

      {/* Top Cards */}
      <div className={styles.grid4}>
        <div className={styles.topCard}>
          <BarChart3
            className={styles.graphIcon}
            onClick={() => openGraphModal("Total Revenue Trend", totalRevenue, "revenue")}
          />
          <div>
            <p>Total Revenue</p>
            <h3>${formatCurrency(totalRevenue)}</h3>
          </div>
          <div className={styles.iconBox}>
            <DollarSign size={18}/>
          </div>
        </div>

        <div className={styles.topCard}>
          <BarChart3
            className={styles.graphIcon}
            onClick={() => openGraphModal("User Payout Trend", userPayout, "payout")}
          />
          <div>
            <p>User Payout</p>
            <h3>${formatCurrency(userPayout)}</h3>
          </div>
          <div className={styles.iconBox}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect width="20" height="14" x="2" y="5" rx="2"></rect>
              <line x1="2" x2="22" y1="10" y2="10"></line>
            </svg>
          </div>
        </div>

        <div className={styles.topCard}>
          <BarChart3
            className={styles.graphIcon}
            onClick={() => openGraphModal("QA Profit Trend", qaProfit, "profit")}
          />
          <div>
            <p>QA Profit</p>
            <h3>${formatCurrency(qaProfit)}</h3>
          </div>
          <div className={styles.iconBox}>
            <TrendingUp size={18}/>
          </div>
        </div>

        <div className={styles.topCard}>
          <BarChart3
            className={styles.graphIcon}
            onClick={() => openGraphModal("VL Fee Trend", vlFee, "vlfee")}
          />
          <div>
            <p>VL Fee</p>
            <h3>${formatCurrency(vlFee)}</h3>
            <span>{(displayedVlFeeRate * 100).toFixed(1)}% of revenue</span>
          </div>
          <div className={styles.iconBox}>
            <Percent size={18}/>
          </div>
        </div>
      </div>

      {/* Small Cards */}
      <div className={styles.grid5}>
        <div className={styles.smallCard}>
          <BarChart3
            className={styles.graphIcon}
            onClick={() => openGraphModal("Pending Tickets Trend", totalPendingTickets, "pending")}
          />
          <p style={{fontSize: "12px", marginBottom: "8px"}}>Pending Tickets</p>
          <div className={styles.countRow}>
            <h3>{formatNumber(totalPendingTickets)}</h3>
            <div className={`${styles.iconBg} ${styles.orange}`}>
              <Clock size={16}/>
            </div>
          </div>
        </div>

        <div className={styles.smallCard}>
          <BarChart3
            className={styles.graphIcon}
            onClick={() => openGraphModal("Listed Tickets Trend", listedTickets, "listed")}
          />
          <p style={{fontSize: "12px", marginBottom: "8px"}}>Listed Tickets</p>
          <div className={styles.countRow}>
            <h3>{formatNumber(listedTickets)}</h3>
            <div className={`${styles.iconBg} ${styles.blue}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"></path>
                <circle cx="7.5" cy="7.5" r=".5" fill="currentColor"></circle>
              </svg>
            </div>
          </div>
        </div>

        <div className={styles.smallCard}>
          <BarChart3
            className={styles.graphIcon}
            onClick={() => openGraphModal("Sold Tickets Trend", realSoldTickets, "sold")}
          />
          <p style={{fontSize: "12px", marginBottom: "8px"}}>Sold Tickets</p>
          <div className={styles.countRow}>
            <h3>{formatNumber(realSoldTickets)}</h3>
            <div className={`${styles.iconBg} ${styles.green}`}>
              <CheckCircle size={16}/>
            </div>
          </div>
        </div>

        <div className={styles.smallCard}>
          <BarChart3
            className={styles.graphIcon}
            onClick={() => openGraphModal("Settled Tickets Trend", settledTickets, "settlement")}
          />
          <p style={{fontSize: "12px", marginBottom: "8px"}}>Settled Tickets</p>
          <div className={styles.countRow}>
            <h3>{formatNumber(settledTickets)}</h3>
            <div className={`${styles.iconBg} ${styles.orange}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"></path>
              </svg>
            </div>
          </div>
        </div>

        <div className={styles.smallCard}>
          <BarChart3
            className={styles.graphIcon}
            onClick={() => openGraphModal("Avg Ticket Price Trend", avgTicketPrice, "avgprice")}
          />
          <p style={{fontSize: "12px", marginBottom: "8px"}}>Avg Ticket Price</p>
          <div className={styles.countRow}>
            <h3>${formatCurrency(avgTicketPrice, 0)}</h3>
            <div className={`${styles.iconBg} ${styles.violet}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="6"></circle>
                <circle cx="12" cy="12" r="2"></circle>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Ticket Status */}
      <div className={styles.ticketSection}>
        <h3 className={styles.sectionTitle}>Ticket Status Breakdown</h3>
        <div className={styles.statusGrid}>
          <div className={`${styles.statusCard} ${styles.orangeBg}`}>
            <BarChart3 className={styles.graphIcon} onClick={() => openGraphModal("To Be Verified", toBeVerifiedTickets, "To Be Verified")}/>
            <h3 className={styles.orangeText}>{formatNumber(toBeVerifiedTickets)}</h3>
            <p style={{fontSize: "11px"}}>To Be Verified</p>
          </div>
          <div className={`${styles.statusCard} ${styles.blueBg}`}>
            <BarChart3 className={styles.graphIcon} onClick={() => openGraphModal("Verified", verifiedTickets, "Verified")}/>
            <h3 className={styles.blueText}>{formatNumber(verifiedTickets)}</h3>
            <p style={{fontSize: "11px"}}>Verified</p>
          </div>
          <div className={`${styles.statusCard} ${styles.indigoBg}`}>
            <BarChart3 className={styles.graphIcon} onClick={() => openGraphModal("Listed", listedTickets, "Listed")}/>
            <h3 className={styles.indigoText}>{formatNumber(listedTickets)}</h3>
            <p style={{fontSize: "11px"}}>Listed</p>
          </div>
          <div className={`${styles.statusCard} ${styles.greenBg}`}>
            <BarChart3 className={styles.graphIcon} onClick={() => openGraphModal("Sold", realSoldTickets, "Sold")}/>
            <h3 className={styles.greenText}>{formatNumber(realSoldTickets)}</h3>
            <p style={{fontSize: "11px"}}>Sold</p>
          </div>
          <div className={`${styles.statusCard} ${styles.redBg}`}>
            <BarChart3 className={styles.graphIcon} onClick={() => openGraphModal("Delisted", delistedTickets, "Delisted")}/>
            <h3 className={styles.redText}>{formatNumber(delistedTickets)}</h3>
            <p style={{fontSize: "11px"}}>Delisted</p>
          </div>
          <div className={`${styles.statusCard} ${styles.grayBg}`}>
            <BarChart3 className={styles.graphIcon} onClick={() => openGraphModal("Unsold", unsoldTickets, "Unsold")}/>
            <h3 className={styles.grayText}>{formatNumber(unsoldTickets)}</h3>
            <p style={{fontSize: "11px"}}>Unsold</p>
          </div>
        </div>
      </div>

      {/* Potential */}
      <div className={styles.grid3}>
        <div className={styles.potentialCardGreen}>
          <BarChart3
            className={styles.graphIcon}
            onClick={() => openGraphModal("Total Potential Trend", potentialPayout, "potential")}
          />
          <p>Total Potential</p>
          <h2>${formatCurrency(potentialPayout, 0)}</h2>
          {(totalPendingTickets + listedTickets) > 0 && (
            <small style={{fontSize: '11px', color: '#666'}}>
              ({totalPendingTickets + listedTickets} pipeline tickets)
            </small>
          )}
        </div>
        <div className={styles.potentialCardBlue}>
          <p>Self Pricing</p>
          <h2>${formatCurrency(displayedSelfPricingPotential, 0)}</h2>
          <small style={{fontSize: '11px', color: '#4f8ef7'}}>
            {/* {spUserCount === 0 && cxUserCount === 0 && untaggedUserCount > 0 ? 'No users tagged yet' : `${spUserCount} users · ${spPendingCount} tickets`} */}
          </small>
        </div>
        <div className={styles.potentialCardGrey}>
          <p>Concierge</p>
          <h2>${formatCurrency(displayedConciergePotential, 0)}</h2>
          <small style={{fontSize: '11px', color: '#9ca3af'}}>
            {/* {spUserCount === 0 && cxUserCount === 0 && untaggedUserCount > 0
              ? `${untaggedUserCount} users not tagged yet`
              : `${cxUserCount} users · ${cxPendingCount} tickets`} */}
          </small>
        </div>
      </div>

      {/* Bottom Grid - Redesigned for Alignment */}
      <div className={styles.bottomGrid}>
 {/* Top Sales by League - With proper column headers and alignment */}
<div className={styles.bottomCard}>
  <h3 className={styles.sectionTitle}>Top Sales by League</h3>
  
  {/* Column Headers */}
  <div className={styles.leagueHeaders}>
    <span className={styles.leagueHeaderName}>League</span>
    <span className={styles.leagueHeaderSold}>SOLD</span>
    <span className={styles.leagueHeaderRevenue}>REVENUE</span>
    <span className={styles.leagueHeaderProfit}>PROFIT</span>
  </div>
  
  <div className={styles.salesList}>
    {leagueBreakdown.map((item, idx) => (
      <div key={item.leagueName} className={styles.salesRow}>
        <div className={styles.salesLeague}>
          <span className={styles[`badge${(idx % 4) + 1}`]}></span>
          <span className={styles.leagueName}>{item.leagueName}</span>
        </div>
        <div className={styles.salesStats}>
          <span className={styles.sold}>{item.soldCount}</span>
          <span className={styles.revenue}>${formatCurrency(item.revenue)}</span>
          <span className={styles.profit}>${formatCurrency(item.profit)}</span>
        </div>
      </div>
    ))}
    {leagueBreakdown.length === 0 && <p className={styles.noData}>No league data available</p>}
  </div>
</div>

{/* QA Marketplace Fee - With proper column headers and alignment */}
<div className={styles.bottomCard}>
  <h3 className={styles.sectionTitle}>QA Marketplace Profit</h3>
  
  {/* Column Headers */}
  <div className={styles.marketplaceHeaders}>
    <span className={styles.marketplaceHeaderMarketplace}>Marketplace</span>
    <span className={styles.marketplaceHeaderSold}>SOLD</span>
    <span className={styles.marketplaceHeaderFee}>Profit</span>
    {/* <span className={styles.marketplaceHeaderRate}>RATE</span> */}
    <span className={styles.marketplaceHeaderPercent}>%</span>
  </div>
  
  <div className={styles.salesList}>
    {qaFeeByMarketplaceSorted.length > 0 ? (
      qaFeeByMarketplaceSorted.map((mp, idx) => {
        const pct = totalQaFeeFromMarketplaces > 0 
          ? (mp.profit / totalQaFeeFromMarketplaces) * 100 
          : 0;
        const avgRate = mp.revenue > 0 ? (mp.profit / mp.revenue) * 100 : 0;
        return (
          <div key={mp.name} className={styles.salesRow}>
            <div className={styles.salesLeague}>
              <span className={styles[`badge${(idx % 4) + 1}`]}></span>
              <span className={styles.leagueName}>{mp.name}</span>
            </div>
            <div className={styles.salesStatsMarketplace}>
              <span className={styles.sold}>{mp.soldCount}</span>
              <span className={styles.revenue}>${formatCurrency(mp.profit, 0)}</span>
              {/* <span className={styles.profit}>{avgRate.toFixed(1)}%</span> */}
              <span className={styles.percent}>{pct.toFixed(1)}%</span>
            </div>
          </div>
        );
      })
    ) : (
      <p className={styles.noData}>No QA marketplace profit data available</p>
    )}
  </div>
  
  {/* Total row */}
  <div className={styles.vlFeeTotalRow}>
    <strong>Total QA Marketplace Profit</strong>
    <strong className={styles.vlFeeTotalAmount}>${formatCurrency(totalQaFeeFromMarketplaces, 0)}</strong>
  </div>
</div>
      </div>

      {/* Inventory Timing */}
      <div className={styles.inventoryTimingSection}>
        <div className={styles.inventoryTimingHeader}>
          <Clock size={18} style={{marginRight: 6}} />
          <strong>Inventory Timing</strong>
        </div>
        <div className={styles.timingGrid}>
          <div className={styles.timingCard}>
            <div className={styles.timingCardHeader}>
              <AlertCircle size={18} style={{marginRight: 6, color: '#ef4444'}} />
              <strong>Blocked Delist Attempts</strong>
            </div>
            <p className={styles.timingCardSubtitle}>Users trying to delist within 3hrs of game time</p>
            <div className={styles.timingBigNumber} style={{color: '#ef4444'}}>{blockedDelistAttempts}</div>
            <p className={styles.timingPeriod}>Last 30 Days</p>
            <p className={styles.timingRecentLabel}>RECENT ATTEMPTS</p>
          </div>

          <div className={styles.timingCard}>
            <div className={styles.timingCardHeader}>
              <Clock size={18} style={{marginRight: 6}} />
              <strong>Delist Timing</strong>
            </div>
            <p className={styles.timingCardSubtitle}>When do users delist relative to event?</p>
            {delistTimingBuckets.map(bucket => {
              const pct = delistTimingTotal > 0 ? (bucket.count / delistTimingTotal) * 100 : 0;
              const width = delistTimingMax > 0 ? (bucket.count / delistTimingMax) * 100 : 0;
              return (
              <div key={bucket.label} className={styles.timingBarRow}>
                <span className={styles.timingBarLabel}>{bucket.label}</span>
                <div className={styles.timingBarTrack}>
                  <div className={styles.timingBarFill} style={{width: `${width}%`, background: '#f59e0b'}}></div>
                </div>
                <span className={styles.timingBarCount}>{bucket.count} ({pct.toFixed(1)}%)</span>
              </div>
              );
            })}
          </div>

          <div className={styles.timingCard}>
            <div className={styles.timingCardHeader}>
              <BarChart3 size={18} style={{marginRight: 6}} />
              <strong>List Timing</strong>
            </div>
            <p className={styles.timingCardSubtitle}>When do users list relative to event?</p>
            {listTimingTotal > 0 && (
              <div className={styles.listTimingHighlight}>
                <span className={styles.listTimingAvg}>{listTimingAvg.toFixed(1)}d</span>
                <small>Avg Before Event ({listTimingTotal} total)</small>
              </div>
            )}
            {listTimingBuckets.map(bucket => {
              const pct = listTimingTotal > 0 ? (bucket.count / listTimingTotal) * 100 : 0;
              const width = listTimingMax > 0 ? (bucket.count / listTimingMax) * 100 : 0;
              return (
                <div key={bucket.label} className={styles.timingBarRow}>
                  <span className={styles.timingBarLabel}>{bucket.label}</span>
                  <div className={styles.timingBarTrack}>
                    <div className={styles.timingBarFill} style={{width: `${width}%`, background: '#10b981'}}></div>
                  </div>
                  <span className={styles.timingBarCount}>{bucket.count} ({pct.toFixed(1)}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Signup Funnel */}
      <div className={styles.signupFunnelSection}>
        <div className={styles.signupFunnelHeader}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
            <BarChart3 size={20} style={{color: '#4f46e5'}} />
            <strong className={styles.signupFunnelTitle}>Signup Funnel</strong>
          </div>
          <p className={styles.signupFunnelSubtitle}>
            Track where users drop off during signup ({sfStarted} started, {sfCompleted} completed)
          </p>
          <div className={styles.signupFunnelPeriodRow}>
            {([7, 30, 90] as const).map(p => (
              <button
                key={p}
                className={`${styles.signupPeriodBtn} ${signupFunnelPeriod === p ? styles.signupPeriodBtnActive : ''}`}
                onClick={() => setSignupFunnelPeriod(p)}
              >{p}d</button>
            ))}
          </div>
        </div>

        <div className={styles.signupSummaryCards}>
          <div className={styles.signupSummaryCard}>
            <div className={styles.signupSummaryValue}>{sfStarted}</div>
            <div className={styles.signupSummaryLabel}>Started</div>
          </div>
          <div className={styles.signupSummaryCard}>
            <div className={styles.signupSummaryValue}>{sfCompleted}</div>
            <div className={styles.signupSummaryLabel}>Completed</div>
          </div>
          <div className={styles.signupSummaryCard}>
            <div className={`${styles.signupSummaryValue} ${styles.signupRateValue}`}>{sfCompletionRate}%</div>
            <div className={styles.signupSummaryLabel}>Completion Rate</div>
          </div>
        </div>

        <div className={styles.signupBarChart}>
          {signupSteps.map(step => {
            const barWidth = signupStepsMax > 0 ? (step.count / signupStepsMax) * 100 : 0;
            return (
              <div key={step.label} className={styles.signupBarRow}>
                <div className={styles.signupBarLabel}>{step.label}</div>
                <div className={styles.signupBarTrack}>
                  <div className={styles.signupBarFill} style={{ width: `${barWidth}%` }}></div>
                </div>
                <div className={styles.signupBarCount}>{step.count}</div>
              </div>
            );
          })}
        </div>

        <div className={styles.signupStepTable}>
          <div className={styles.signupStepTableHeader}>STEP-BY-STEP CONVERSION</div>
          {signupSteps.map((step, idx) => {
            const rate = sfStarted > 0 ? Math.round((step.count / sfStarted) * 100) : 0;
            const dropOff = idx > 0 ? signupSteps[idx - 1].count - step.count : 0;
            return (
              <div key={step.label} className={styles.signupStepRow}>
                <div className={styles.signupStepNum}>{idx + 1}.</div>
                <div className={styles.signupStepName}>{step.label}</div>
                <div className={styles.signupStepCount}>{step.count}</div>
                <div className={styles.signupStepRate}>{rate}%</div>
                {idx > 0 && <div className={styles.signupStepDrop}>{dropOff > 0 ? `-${dropOff}` : '—'}</div>}
                {idx === 0 && <div className={styles.signupStepDrop}>—</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Graph Modal */}
      {openGraph && (
        <GraphModal
          title={graphTitle}
          value={graphValue}
          dataType={graphDataType}
          onClose={() => setOpenGraph(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;